import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Divider,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Tooltip,
  Popper,
  Paper,
  FormControlLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  ListSubheader,
  Snackbar
} from '@mui/material';
import { NavigateBefore, NavigateNext } from '@mui/icons-material';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Clock,
  User,
  MapPin,
  ChatCircleText,
  CheckCircle,
  XCircle,
  ClockCounterClockwise,
  CaretLeft,
  CaretRight,
  CaretDown,
  Plus,
  CalendarCheck,
  CalendarBlank,
  Columns
} from '@phosphor-icons/react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useAuth } from '../../context/AuthContext';
import { useBusiness } from '../../context/BusinessContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import api from '../../utils/api';
import {
  getManualOrPublicEmbedGuestName,
  getBookingGuestDisplayName,
  getBookingGuestEmailLine
} from '../../utils/unifiedBookingDisplay';
import DayBookingsModal from './DayBookingsModal';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addMonths, addDays, addYears, eachDayOfInterval, isSameDay, isSameMonth, startOfDay, endOfDay } from 'date-fns';
import { getAvatarSrc, getStaffMemberDisplayPhoto } from '../../utils/avatarConstants';
// Calendar sync settings not used in this view to match staff calendar

const UnifiedCalendar = ({
  userType = 'client',
  initialFilters = {},
  businessId: propBusinessId,
  onEventClick,
  onBookingClick,
  onBookingChanged,
  refreshToken = 0,
  onCreateNewClick,
  onCreateNewAppointment,
  onCreateNewEvent,
}) => {
  const { _user } = useAuth();
  const { selectedBusiness } = useBusiness();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const parseViewFromUrl = (v) => (v === 'week' || v === 'month' ? v : 'day');
  const parseDateFromUrl = (d) => {
    if (!d) return null;
    const parsed = new Date(d + 'T12:00:00');
    return isNaN(parsed.getTime()) ? null : parsed;
  };
  const toDateStr = (date) => format(date, 'yyyy-MM-dd');

  const viewFromUrl = parseViewFromUrl(searchParams.get('view'));
  const dateFromUrl = parseDateFromUrl(searchParams.get('date'));
  const initialDateRef = useRef(dateFromUrl || new Date());
  const initialViewRef = useRef(viewFromUrl);

  const [currentDate, setCurrentDate] = useState(initialDateRef.current);
  const currentDateRef = useRef(currentDate);
  const fetchSeqRef = useRef(0); // Monotonic id so only the latest fetch response is applied
  const inFlightRequestKeyRef = useRef(null); // Dedupe identical concurrent requests without dropping new ranges
  const fullCalendarDateRangeRef = useRef(null); // Store date range from FullCalendar datesSet using ref to avoid infinite loops
  const [fcMonthRangeVersion, setFcMonthRangeVersion] = useState(0); // Bumped when FullCalendar reports a new month grid range
  const [view, setView] = useState(initialViewRef.current);
  const lastUrlRef = useRef(null); // null so first "sync to URL" effect runs and writes params
  const [bookings, setBookings] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [editTeamMembersOpen, setEditTeamMembersOpen] = useState(false);
  const [availableTeamMembersForEdit, setAvailableTeamMembersForEdit] = useState([]);
  const [selectedTeamMembersForEdit, setSelectedTeamMembersForEdit] = useState([]);
  const [editLocationType, setEditLocationType] = useState('on_site');
  const [editLocationAddress, setEditLocationAddress] = useState('');
  const [editVideoLink, setEditVideoLink] = useState('');
  const [editSupplierNotes, setEditSupplierNotes] = useState('');
  const [editBookingSaving, setEditBookingSaving] = useState(false);
  const [editBookingError, setEditBookingError] = useState('');
  const [editBookingToast, setEditBookingToast] = useState({ open: false, message: '' });
  const [businessId, setBusinessId] = useState(propBusinessId || selectedBusiness?.business._id || null);
  const [dateRange, setDateRange] = useState(null); // For custom header
  const [teamMembers, setTeamMembers] = useState([]); // For day view staff columns
  const [teamMemberShifts, setTeamMemberShifts] = useState([]); // For staff avatars/context only
  const [openingHours, setOpeningHours] = useState(null);
  const [openingHoursLoading, setOpeningHoursLoading] = useState(false);
  const [calendarSettings, setCalendarSettings] = useState({ startOfWeek: 1 }); // Default to Monday
  const [showCancelled, setShowCancelled] = useState(true); // Default to showing cancelled appointments
  // Day/week views show the full 24 hours by default; this trims them to opening hours.
  const [onlyOpeningHours, setOnlyOpeningHours] = useState(() => {
    try {
      return localStorage.getItem('idotoo_calendar_only_opening_hours') === 'true';
    } catch {
      return false;
    }
  });
  const [calendarRenderKey, setCalendarRenderKey] = useState(0); // Force re-render of disabled slot logic
  const [eventTooltip, setEventTooltip] = useState({
    open: false,
    anchorEl: null,
    title: '',
    assignees: ''
  });
  const filters = {
    status: initialFilters.status || 'all',
    serviceType: initialFilters.serviceType || 'all'
  };
  const calendarRef = useRef(null);
  const [createNewAnchorEl, setCreateNewAnchorEl] = useState(null);
  const createNewMenuOpen = Boolean(createNewAnchorEl);
  // Filter by appointment type / event tag. Empty = show everything.
  const [typeFilterAnchorEl, setTypeFilterAnchorEl] = useState(null);
  const [typeFilters, setTypeFilters] = useState([]);
  const [serviceTypesList, setServiceTypesList] = useState([]);
  const [eventTypesList, setEventTypesList] = useState([]);
  const [mobileViewMode, setMobileViewMode] = useState('week');
  const [mobileWeekStart, setMobileWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [mobileMonthStart, setMobileMonthStart] = useState(() => startOfMonth(new Date()));
  const [dayModal, setDayModal] = useState({ open: false, date: null });
  const [scheduleViewMode, setScheduleViewMode] = useState('calendar');
  const [listViewBookings, setListViewBookings] = useState([]);
  const [listViewEvents, setListViewEvents] = useState([]);
  const [listViewLoading, setListViewLoading] = useState(false);

  const normaliseId = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value._id) return String(value._id);
    if (value.toString) return value.toString();
    return '';
  };

  const normaliseTagKey = (name) => String(name || '').trim().toLowerCase();
  const getBookingTypeFilterKey = (booking) => `svc:${normaliseId(booking?.serviceType?._id || booking?.serviceType)}`;
  const getEventTypeFilterKey = (event) => `tag:${normaliseTagKey(event?.eventType)}`;

  const getAssignmentIds = (assignment) => {
    if (!assignment) return [];
    return [
      normaliseId(assignment.member?._id || assignment.member),
      normaliseId(assignment.staffMember?._id || assignment.staffMember)
    ].filter(Boolean);
  };

  const getMemberCanonicalId = (member) => (
    normaliseId(
      member?.staffMemberId ||
      member?.businessMember?._id ||
      member?.businessMember ||
      member?._id
    )
  );

  const getMemberDisplayName = useCallback((member) => {
    if (!member) return 'Unnamed';

    const businessMemberUserName = member.businessMember?.user
      ? `${member.businessMember.user.firstName || ''} ${member.businessMember.user.lastName || ''}`.trim()
      : '';
    const directName = (member.firstName || member.lastName)
      ? `${member.firstName || ''} ${member.lastName || ''}`.trim()
      : '';
    const staffName = String(
      member.assignment?.staffMemberName ||
      member.assignment?.staffMember?.name ||
      member.staffMember?.name ||
      member.staffMemberName ||
      member.staffName ||
      member.businessMember?.name ||
      member.businessMember?.staffMemberName ||
      businessMemberUserName ||
      directName ||
      member.member?.staffMemberName ||
      ''
    ).trim();
    if (staffName) return staffName;

    const accountName = `${member.user?.firstName || ''} ${member.user?.lastName || ''}`.trim();
    if (accountName) return accountName;

    const explicitName = String(member.name || '').trim();
    if (explicitName && !['unnamed', 'unknown', 'unknown user', 'no name'].includes(explicitName.toLowerCase())) return explicitName;

    return 'Unnamed';
  }, []);

  const getBookingDisplayPerson = useCallback((booking) => {
    const primaryAssignment = booking?.assignedTeamMembers?.[0] || null;
    const assignmentMember = primaryAssignment?.member || null;
    const assignmentStaff = primaryAssignment?.staffMember || null;

    const assignedName = getMemberDisplayName({
      ...assignmentMember,
      assignment: primaryAssignment,
      staffMember: assignmentStaff
    });

    const assignedPhoto = getStaffMemberDisplayPhoto({
      ...assignmentMember,
      user: assignmentMember?.user || assignmentStaff?.user || null,
      profileImage: assignmentMember?.profileImage || assignmentStaff?.profileImage || null
    });

    if (assignedName !== 'Unnamed') {
      return {
        name: assignedName,
        avatar: getAvatarSrc(assignedPhoto),
      };
    }

    const manualGuest = getManualOrPublicEmbedGuestName(booking);
    const client = booking?.client || null;
    if (manualGuest) {
      return {
        name: manualGuest,
        avatar: getAvatarSrc(client?.profilePicture || client?.profileImage || null),
      };
    }

    return {
      name: getBookingGuestDisplayName(booking),
      avatar: getAvatarSrc(client?.profilePicture || client?.profileImage || null),
    };
  }, [getMemberDisplayName]);

  const getAssignmentDisplayPerson = useCallback((assignment) => {
    if (!assignment) return null;
    const assignmentMember = assignment?.member || null;
    const assignmentStaff = assignment?.staffMember || null;
    const assignmentMemberObject = assignmentMember && typeof assignmentMember === 'object' ? assignmentMember : {};
    const assignmentStaffObject = assignmentStaff && typeof assignmentStaff === 'object' ? assignmentStaff : {};

    const assignedName = getMemberDisplayName({
      ...assignmentMemberObject,
      assignment,
      staffMember: assignmentStaffObject
    });
    const assignedPhoto = getStaffMemberDisplayPhoto({
      ...assignmentMemberObject,
      user: assignmentMemberObject?.user || assignmentStaffObject?.user || null,
      profileImage: assignmentMemberObject?.profileImage || assignmentStaffObject?.profileImage || null
    });

    if (!assignedName || assignedName === 'Unnamed') return null;
    return {
      name: assignedName,
      avatar: getAvatarSrc(assignedPhoto),
    };
  }, [getMemberDisplayName]);

  const getBookingDisplayPeople = useCallback((booking) => {
    const people = (booking?.assignedTeamMembers || [])
      .map((assignment) => getAssignmentDisplayPerson(assignment))
      .filter(Boolean);
    if (people.length > 0) return people;

    const client = booking?.client || null;
    return [{
      name: getBookingGuestDisplayName(booking) || getMemberDisplayName({ user: client }),
      avatar: getAvatarSrc(client?.profilePicture || client?.profileImage || null),
    }];
  }, [getAssignmentDisplayPerson, getMemberDisplayName]);

  const getEventDisplayPerson = useCallback((calendarEvent) => {
    const primaryAssignment = calendarEvent?.assignedTeamMembers?.[0] || null;
    if (!primaryAssignment) {
      return {
        name: 'Unassigned',
        avatar: getAvatarSrc(null),
      };
    }

    const assignmentMember = primaryAssignment?.member || null;
    const assignmentStaff = primaryAssignment?.staffMember || null;

    const assignedName = getMemberDisplayName({
      ...assignmentMember,
      assignment: primaryAssignment,
      staffMember: assignmentStaff
    });

    const assignedPhoto = getStaffMemberDisplayPhoto({
      ...assignmentMember,
      user: assignmentMember?.user || assignmentStaff?.user || null,
      profileImage: assignmentMember?.profileImage || assignmentStaff?.profileImage || null
    });

    return {
      name: assignedName !== 'Unnamed' ? assignedName : 'Unassigned',
      avatar: getAvatarSrc(assignedPhoto),
    };
  }, [getMemberDisplayName]);

  const getEventDisplayPeople = useCallback((calendarEvent) => {
    const people = (calendarEvent?.assignedTeamMembers || [])
      .map((assignment) => getAssignmentDisplayPerson(assignment))
      .filter(Boolean);
    if (people.length > 0) return people;
    return [{ name: 'Unassigned', avatar: getAvatarSrc(null) }];
  }, [getAssignmentDisplayPerson]);

  // Sync FROM URL (browser back/forward or initial load with params)
  useEffect(() => {
    const v = parseViewFromUrl(searchParams.get('view'));
    const d = parseDateFromUrl(searchParams.get('date'));
    const dateToUse = d || new Date();
    const key = `${v}-${toDateStr(dateToUse)}`;
    if (lastUrlRef.current && key === `${lastUrlRef.current.view}-${lastUrlRef.current.date}`) return;
    const hadParams = searchParams.get('view') != null || searchParams.get('date') != null;
    if (hadParams) lastUrlRef.current = { view: v, date: toDateStr(dateToUse) };
    setView(v);
    setCurrentDate(dateToUse);
    currentDateRef.current = dateToUse;
    const api = calendarRef.current?.getApi();
    if (api) {
      const fcView = v === 'month' ? 'dayGridMonth' : v === 'week' ? 'timeGridWeek' : 'timeGridDay';
      api.changeView(fcView, dateToUse);
      api.gotoDate(dateToUse);
    }
  }, [searchParams]);

  // Sync TO URL when view or currentDate change (user navigation)
  useEffect(() => {
    const key = `${view}-${toDateStr(currentDate)}`;
    const last = lastUrlRef.current;
    if (last && key === `${last.view}-${last.date}`) return;
    lastUrlRef.current = { view, date: toDateStr(currentDate) };
    setSearchParams({ view, date: toDateStr(currentDate) }, { replace: false });
  }, [view, currentDate, setSearchParams]);

  const handleCreateNewClick = (event) => {
    // Always set local state for the menu
    setCreateNewAnchorEl(event.currentTarget);
    // Also call the passed handler if it exists (for dashboard state management)
    if (onCreateNewClick) {
      onCreateNewClick(event);
    }
  };

  const handleCreateNewClose = () => {
    setCreateNewAnchorEl(null);
  };

  const handleCreateNewAppointment = () => {
    handleCreateNewClose();
    if (onCreateNewAppointment) {
      onCreateNewAppointment();
    }
  };

  const handleCreateNewEvent = () => {
    handleCreateNewClose();
    if (onCreateNewEvent) {
      onCreateNewEvent();
    }
  };

  // Keep currentDateRef in sync with currentDate
  useEffect(() => {
    currentDateRef.current = currentDate;
  }, [currentDate]);

  // Update businessId when selectedBusiness changes
  useEffect(() => {
    if (selectedBusiness?.business?._id && userType === 'supplier') {
      const newBusinessId = selectedBusiness.business._id;
      if (newBusinessId !== businessId) {
        setBusinessId(newBusinessId);
      }
    }
  }, [selectedBusiness?.business?._id, userType, businessId]);

  // Fetch calendar settings
  useEffect(() => {
    if (businessId && userType === 'supplier') {
      const fetchCalendarSettings = async () => {
        try {
          const response = await api.get(`/api/business/${businessId}/calendar-settings`);
          if (response.data) {
            const startOfWeekSetting = Number(response.data.startOfWeek);
            setCalendarSettings({
              ...response.data,
              startOfWeek: Number.isInteger(startOfWeekSetting) && startOfWeekSetting >= 0 && startOfWeekSetting <= 6
                ? startOfWeekSetting
                : 1,
            });
          }
        } catch (error) {}
      };
      fetchCalendarSettings();
    }
  }, [businessId, userType]);

  // Keep mobile week aligned when business week-start setting loads/changes
  useEffect(() => {
    const weekStartsOn = calendarSettings?.startOfWeek ?? 1;
    setMobileWeekStart((prev) => startOfWeek(prev, { weekStartsOn }));
  }, [calendarSettings?.startOfWeek]);

  useEffect(() => {
    if (!businessId || userType !== 'supplier') {
      setOpeningHours(null);
      setOpeningHoursLoading(false);
      return;
    }

    setOpeningHoursLoading(true);
    api.get(`/api/opening-hours/${businessId}`)
      .then((response) => {
        setOpeningHours({
          hours: response.data?.openingHours || {},
          closedDates: response.data?.closedDates || [],
        });
      })
      .catch(() => setOpeningHours(null))
      .finally(() => setOpeningHoursLoading(false));
  }, [businessId, userType]);

  // Fetch team members for day view
  useEffect(() => {
    if (businessId && userType === 'supplier') {
      fetchTeamMembers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [businessId, userType]);

  // Fetch appointment types and saved event tags for the type filter dropdown
  useEffect(() => {
    if (!businessId || userType !== 'supplier') {
      setServiceTypesList([]);
      setEventTypesList([]);
      return;
    }
    setTypeFilters([]); // Selections from another business are meaningless here
    Promise.all([
      api.get('/api/service-types', { params: { businessId } }).catch(() => null),
      api.get('/api/event-types', { params: { businessId, isActive: true } }).catch(() => null)
    ]).then(([serviceTypesResponse, eventTypesResponse]) => {
      setServiceTypesList(serviceTypesResponse?.data?.serviceTypes || []);
      setEventTypesList(eventTypesResponse?.data?.eventTypes || []);
    });
  }, [businessId, userType]);

  // Saved event tags plus any legacy tags present on loaded events
  const eventTagOptions = useMemo(() => {
    const tags = new Map();
    eventTypesList.forEach((type) => {
      const key = normaliseTagKey(type.name);
      if (key) tags.set(key, { key, name: type.name, color: type.color || '#ff9800' });
    });
    events.forEach((event) => {
      const key = normaliseTagKey(event.eventType);
      if (key && !tags.has(key)) {
        tags.set(key, { key, name: event.eventType, color: event.color || '#ff9800' });
      }
    });
    return Array.from(tags.values()).sort((a, b) => a.name.localeCompare(b.name));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- normaliseTagKey is a pure helper
  }, [eventTypesList, events]);

  const toggleTypeFilter = useCallback((key) => {
    setTypeFilters((prev) => (
      prev.includes(key) ? prev.filter((existing) => existing !== key) : [...prev, key]
    ));
  }, []);

  // Fetch team member shifts for staff avatars/context only.
  useEffect(() => {
    if (businessId && userType === 'supplier') {
      // Availability and clickability are based on opening hours, not these shifts.
      if (view === 'day') {
        fetchTeamMemberShifts();
      } else if (view === 'week' || view === 'month') {
        const start = getStartOfPeriod(currentDate, view);
        const end = getEndOfPeriod(currentDate, view);
        fetchTeamMemberShiftsForRange(start, end);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [businessId, userType, view, currentDate]);

  const fetchTeamMemberShiftsForRange = async (startDate, endDate) => {
    if (!businessId) return;
    
    try {
      const response = await api.get(`/api/team-member-shifts/business/${businessId}`, {
        params: {
          start: startDate.toISOString(),
          end: endDate.toISOString()
        }
      });
      setTeamMemberShifts(response.data.shifts || []);
    } catch (error) {
      setTeamMemberShifts([]);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      // Fetch staff members from schedules API (like in schedules view)
      const response = await api.get(`/api/supplier/staff-members?businessId=${businessId}`);
      const staffMembers = response.data.staffMembers || [];

      // Transform staff members to match the expected structure for day view
      // Show all active staff members (like in schedules view), even if they don't have BusinessMember
      // For matching bookings, we use BusinessMember ID if available, otherwise staffMember ID
      const transformedMembers = staffMembers
        .filter(sm => sm.status === 'active') // Only active staff members
        .map(sm => {
          // StaffMember structure: { _id, user, name, role, businessMember, profileImage, ... }
          // Prefer BusinessMember ID, but retain StaffMember ID for force-assigned bookings.
          const businessMemberId = normaliseId(sm.businessMember?._id || sm.businessMember);
          const staffMemberId = normaliseId(sm._id);
          const memberId = businessMemberId || staffMemberId;
          
          return {
            _id: memberId, // Use for matching bookings (BusinessMember ID if available)
            staffMemberId, // Keep reference to staff member
            user: sm.user, // User is already populated
            name: sm.name || (sm.user ? `${sm.user.firstName || ''} ${sm.user.lastName || ''}`.trim() : 'Unnamed'),
            role: sm.role || sm.businessMember?.role,
            businessMember: sm.businessMember,
            profileImage: sm.profileImage // StaffMember's profile image (from schedules)
          };
        });

      setTeamMembers(transformedMembers);
    } catch (error) {}
  };

  const fetchTeamMemberShifts = async () => {
    if (!businessId) return;
    
    try {
      const dayStart = new Date(currentDate);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(currentDate);
      dayEnd.setHours(23, 59, 59, 999);

      const response = await api.get(`/api/team-member-shifts/business/${businessId}`, {
        params: {
          start: dayStart.toISOString(),
          end: dayEnd.toISOString()
        }
      });
      setTeamMemberShifts(response.data.shifts || []);
    } catch (error) {
      setTeamMemberShifts([]);
    }
  };

  const fetchBookings = useCallback(async () => {
    // Don't fetch if supplier and no businessId
    if (userType === 'supplier' && !businessId) {
      return;
    }

    let startDate, endDate;
    if (isMobile) {
      const weekStartsOn = calendarSettings?.startOfWeek ?? 1;
      if (mobileViewMode === 'week') {
        startDate = startOfWeek(mobileWeekStart, { weekStartsOn });
        endDate = endOfWeek(mobileWeekStart, { weekStartsOn });
      } else {
        startDate = startOfMonth(mobileMonthStart);
        endDate = endOfMonth(mobileMonthStart);
      }
    } else if (view === 'month' && fullCalendarDateRangeRef.current) {
      startDate = new Date(fullCalendarDateRangeRef.current.start);
      endDate = new Date(fullCalendarDateRangeRef.current.end);
    } else {
      startDate = getStartOfPeriod(currentDate, view);
      endDate = getEndOfPeriod(currentDate, view);
    }

    const params = {
      type: userType,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      status: filters.status,
      serviceType: filters.serviceType
    };

    // Add businessId for supplier bookings
    if (userType === 'supplier' && businessId) {
      params.businessId = businessId;
    }

    // Skip only if an identical request is already in flight; new ranges always fetch.
    const requestKey = JSON.stringify({ ...params, refreshToken });
    if (inFlightRequestKeyRef.current === requestKey) {
      return;
    }
    inFlightRequestKeyRef.current = requestKey;

    const seq = ++fetchSeqRef.current;
    setLoading(true);
    try {
      // Fetch bookings and events in parallel; an events failure must not blank bookings.
      const eventsPromise = (userType === 'supplier' && businessId)
        ? api.get(`/api/events/business/${businessId}`, {
            params: {
              startDate: startDate.toISOString(),
              endDate: endDate.toISOString()
            }
          }).catch(() => null)
        : Promise.resolve(undefined);

      const [response, eventsResponse] = await Promise.all([
        api.get('/api/unified-bookings', { params }),
        eventsPromise
      ]);

      // Ignore stale responses; only the latest request may update state.
      if (seq !== fetchSeqRef.current) return;

      setBookings(response.data.bookings || []);
      if (eventsResponse !== undefined) {
        setEvents(eventsResponse?.data?.events || []);
      }
    } catch (error) {
      if (seq !== fetchSeqRef.current) return;
      // 429 (rate limit) is temporary: keep the currently displayed bookings rather than blanking the calendar.
      if (error.response?.status !== 429) {
        setError('Failed to load bookings');
        setBookings([]);
      }
    } finally {
      if (inFlightRequestKeyRef.current === requestKey) {
        inFlightRequestKeyRef.current = null;
      }
      if (seq === fetchSeqRef.current) {
        setLoading(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [currentDate, view, filters.status, filters.serviceType, businessId, userType, refreshToken, isMobile, mobileViewMode, mobileWeekStart, mobileMonthStart, calendarSettings?.startOfWeek, fcMonthRangeVersion]);

  const fetchListViewBookings = useCallback(async () => {
    if (userType === 'supplier' && !businessId) return;

    setListViewLoading(true);
    try {
      const todayStart = startOfDay(new Date());
      const futureEnd = endOfDay(addYears(todayStart, 5));
      const params = {
        type: userType,
        startDate: todayStart.toISOString(),
        endDate: futureEnd.toISOString(),
        status: filters.status,
        serviceType: filters.serviceType,
        limit: 2000
      };

      if (userType === 'supplier' && businessId) {
        params.businessId = businessId;
      }

      const response = await api.get('/api/unified-bookings', { params });
      setListViewBookings(response.data.bookings || []);

      if (userType === 'supplier' && businessId) {
        const eventsResponse = await api.get(`/api/events/business/${businessId}`, {
          params: {
            startDate: todayStart.toISOString(),
            endDate: futureEnd.toISOString()
          }
        });
        setListViewEvents(eventsResponse.data.events || []);
      } else {
        setListViewEvents([]);
      }
    } catch {
      setListViewBookings([]);
      setListViewEvents([]);
    } finally {
      setListViewLoading(false);
    }
  }, [businessId, filters.serviceType, filters.status, userType]);

  // Fetch bookings when dependencies change
  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  useEffect(() => {
    if (scheduleViewMode !== 'list') return;
    fetchListViewBookings();
  }, [scheduleViewMode, fetchListViewBookings, refreshToken]);

  // Check if a date/time is in the past
  const isPastDate = useCallback((date) => {
    const now = new Date();
    const checkDate = new Date(date);
    return checkDate < now;
  }, []);

  const isPastCalendarDay = useCallback((date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);

    return checkDate < today;
  }, []);

  const getOpeningHoursForDate = useCallback((date) => {
    const hours = openingHours?.hours;
    if (!hours) return null;

    const dayKey = format(new Date(date), 'eeee').toLowerCase();
    return hours[dayKey] || null;
  }, [openingHours]);

  const isClosedDate = useCallback((date) => {
    const dateKey = format(new Date(date), 'yyyy-MM-dd');
    return (openingHours?.closedDates || []).some((closedDate) => {
      if (!closedDate?.date) return false;
      return format(new Date(closedDate.date), 'yyyy-MM-dd') === dateKey;
    });
  }, [openingHours]);

  const isWithinOpeningHours = useCallback((slotDateTime) => {
    if (!openingHours) return true;

    const dayHours = getOpeningHoursForDate(slotDateTime);
    if (!dayHours || !dayHours.isOpen || isClosedDate(slotDateTime)) {
      return false;
    }

    const time = format(new Date(slotDateTime), 'HH:mm');
    return time >= dayHours.openTime && time < dayHours.closeTime;
  }, [openingHours, getOpeningHoursForDate, isClosedDate]);

  // Earliest open / latest close across the week, used to trim the time grids when
  // "Only show opening hours" is on and to scroll the full 24-hour grid to a useful hour.
  const openingHoursWindow = useMemo(() => {
    const hours = openingHours?.hours;
    const openDays = hours
      ? Object.values(hours).filter((day) => day?.isOpen && day.openTime && day.closeTime)
      : [];
    if (openDays.length === 0) return null;
    // Zero-padded HH:mm strings compare correctly lexicographically
    const min = openDays.reduce((acc, day) => (day.openTime < acc ? day.openTime : acc), openDays[0].openTime);
    const max = openDays.reduce((acc, day) => (day.closeTime > acc ? day.closeTime : acc), openDays[0].closeTime);
    return { min, max };
  }, [openingHours]);

  const timeGridSlotRange = useMemo(() => {
    if (onlyOpeningHours && openingHoursWindow) {
      return { min: `${openingHoursWindow.min}:00`, max: `${openingHoursWindow.max}:00` };
    }
    return { min: '00:00:00', max: '24:00:00' };
  }, [onlyOpeningHours, openingHoursWindow]);

  const handleOnlyOpeningHoursChange = useCallback((checked) => {
    setOnlyOpeningHours(checked);
    try {
      localStorage.setItem('idotoo_calendar_only_opening_hours', String(checked));
    } catch {
      // Persistence is best-effort only
    }
  }, []);

  // Supplier calendar eligibility is based on business opening hours, not staff shifts.
  const isTimeSlotEligible = useCallback((slotDateTime) => {
    // Check if this is a past date/time - always disable past slots
    if (isPastDate(slotDateTime)) {
      return false;
    }

    return isWithinOpeningHours(slotDateTime);
  }, [isPastDate, isWithinOpeningHours]);

  // slotLaneClassNames is not used for week view since each td represents the entire row
  // We handle disabled slots manually via DOM manipulation in useEffect to target individual day columns
  const getSlotLaneClassNames = useCallback((_slotInfo) => {
    // Don't apply any classes via slotLaneClassNames for week view
    // We handle it manually in useEffect to target individual day columns
    return '';
  }, []);

  // Apply disabled classes to individual day divs within time slot rows
  // Each time slot row now has 7 divs (one for each day) created via slotLaneContent
  useEffect(() => {
    if (userType !== 'supplier') return;

    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    const viewType = calendarApi.view?.type;
    // Only apply in week view
    if (viewType !== 'timeGridWeek') return;
    
    if (process.env.NODE_ENV === 'development') {}

    // Function to apply disabled slots - can be called immediately or after delay
    const applyDisabledSlots = () => {
      try {
        // Try multiple ways to get the calendar element
        let calendarEl = null;

        // Method 1: Try from ref
        if (calendarRef.current) {
          calendarEl = calendarRef.current.el || calendarRef.current.querySelector?.('.fc');
        }

        // Method 2: Try from API
        if (!calendarEl && calendarApi) {
          calendarEl = calendarApi.el;
        }

        // Method 3: Search DOM for FullCalendar element
        if (!calendarEl) {
          // Look for the FullCalendar root element
          const fcElements = document.querySelectorAll('.fc');
          for (const el of fcElements) {
            // Check if this is a timeGridWeek view
            if (el.querySelector('.fc-timeGridWeek-view') || el.querySelector('.fc-timegrid-slot-table')) {
              calendarEl = el;
              break;
            }
          }
        }

        if (!calendarEl) {
          return false; // Return false to indicate not ready
        }

        // Calendar element found, process disabled slots

        // Get all day divs created by slotLaneContent
        const dayDivs = calendarEl.querySelectorAll('.fc-slot-day-cell');

        if (dayDivs.length === 0) {
          const _slotTable = calendarEl.querySelector('.fc-timegrid-slot-table');
          return;
        }

        let processedCount = 0;
        let disabledCount = 0;
        let _skippedCount = 0;

        dayDivs.forEach((dayDiv, index) => {
          const dateStr = dayDiv.getAttribute('data-slot-date');
          let timeStr = dayDiv.getAttribute('data-slot-time');
          
          if (!dateStr) {
            _skippedCount++;
            return;
          }
          
          // If time is empty, get it from the parent td element
          // The parent <td> has the data-time attribute directly
          if (!timeStr || timeStr === '') {
            // Find the parent td (fc-timegrid-slot-lane) which has data-time attribute
            const parentTd = dayDiv.closest('td.fc-timegrid-slot-lane');
            if (parentTd) {
              // Get the time directly from the td's data-time attribute
              timeStr = parentTd.getAttribute('data-time') || '';
              // Update the data attribute for future reference
              if (timeStr) {
                dayDiv.setAttribute('data-slot-time', timeStr);
              }
            }
          }
          
          // If still no time, skip this div (can't determine eligibility without time)
          if (!timeStr || timeStr === '') {
            _skippedCount++;
            if (index < 5) {}
            return;
          }
          
          // Parse the date
          const [year, month, day] = dateStr.split('-').map(Number);
          
          // Parse the time (format: "HH:MM:SS" or "HH:MM")
          const timeParts = timeStr.split(':');
          const hours = parseInt(timeParts[0], 10) || 0;
          const minutes = parseInt(timeParts[1], 10) || 0;
          
          // Create the full date-time for this specific slot (day + time)
          const slotDateTime = new Date(year, month - 1, day, hours, minutes, 0, 0);
          
          // Validate the date
          if (isNaN(slotDateTime.getTime())) {
            _skippedCount++;
            if (index < 5) {}
            return;
          }
          
          // Check if this specific day+time slot is within opening hours.
          const isEligible = isTimeSlotEligible(slotDateTime);
          const isPast = isPastDate(slotDateTime);
          
          processedCount++;
          
          // Add or remove disabled class on the day div
          if (isEligible) {
            dayDiv.classList.remove('fc-slot-day-disabled');
            dayDiv.classList.remove('fc-slot-past');
          } else {
            dayDiv.classList.add('fc-slot-day-disabled');
            if (isPast) {
              dayDiv.classList.add('fc-slot-past');
            } else {
              dayDiv.classList.remove('fc-slot-past');
            }
            disabledCount++;
            if (disabledCount <= 5) {}
          }
        });

        // Double-check: verify some divs have the disabled class
        const _disabledDivs = calendarEl.querySelectorAll('.fc-slot-day-cell.fc-slot-day-disabled');

        // If no disabled divs were found but we expected some, log a sample of what we processed
        if (disabledCount === 0 && processedCount > 0) {}
      } catch (error) {}
      
      return true; // Return true to indicate success
    };
    
    // Try immediately first
    let timer = null;
    
    if (!applyDisabledSlots()) {
      // If not ready, use retry mechanism with faster intervals
      let retryCount = 0;
      const maxRetries = 15; // Max 1.5 seconds of retries (15 * 100ms)
      
      const retryCheck = () => {
        if (applyDisabledSlots()) {
          // Success, stop retrying
          return;
        }
        
        retryCount++;
        if (retryCount < maxRetries) {
          timer = setTimeout(retryCheck, 100); // Faster retry interval (100ms instead of 200ms)
        }
      };
      
      // Start retrying after a short delay
      timer = setTimeout(retryCheck, 100);
    }

    // Cleanup function
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [teamMemberShifts, view, userType, isTimeSlotEligible, calendarSettings, calendarRenderKey, timeGridSlotRange]);

  // Week boundaries must match FullCalendar's firstDay (calendarSettings.startOfWeek, Monday by
  // default), otherwise bookings on the edge days of the visible grid are never fetched.
  const getStartOfPeriod = (date, view) => {
    const weekStartsOn = calendarSettings?.startOfWeek ?? 1;
    switch (view) {
      case 'month':
        // Start of the week containing the first day of the month
        return startOfWeek(startOfMonth(date), { weekStartsOn });
      case 'week':
        return startOfWeek(date, { weekStartsOn });
      case 'day':
        return startOfDay(date);
      default:
        return date;
    }
  };

  const getEndOfPeriod = (date, view) => {
    const weekStartsOn = calendarSettings?.startOfWeek ?? 1;
    switch (view) {
      case 'month': {
        // End of the week containing the last day of the month, padded by a week to cover
        // FullCalendar's fixed six-week month grid until datesSet reports the exact range.
        const endOfGrid = endOfWeek(endOfMonth(date), { weekStartsOn });
        return endOfDay(addDays(endOfGrid, 7));
      }
      case 'week':
        return endOfWeek(date, { weekStartsOn });
      case 'day':
        return endOfDay(date);
      default:
        return date;
    }
  };

  const getStatusColorForChip = (status) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      case 'cancelled': return 'default';
      case 'completed': return 'info';
      case 'no_show': return 'error';
      case 'rescheduled': return 'secondary';
      case 'awaiting_approval': return 'warning';
      default: return 'default';
    }
  };

  const getDayViewTargetDate = useCallback(() => {
    // When switching from week/month via the Day toggle, default to today.
    // Explicit day clicks in week/month are handled in handleDateClick and pass the clicked date directly.
    if (view === 'week' || view === 'month') {
      return new Date();
    }
    return currentDate;
  }, [view, currentDate]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return <CheckCircle size={16} />;
      case 'pending': return <Clock size={16} />;
      case 'rejected': return <XCircle size={16} />;
      case 'cancelled': return <XCircle size={16} />;
      case 'completed': return <CheckCircle size={16} />;
      case 'no_show': return <XCircle size={16} />;
      case 'rescheduled': return <ClockCounterClockwise size={16} />;
      case 'awaiting_approval': return <Clock size={16} />;
      default: return <Clock size={16} />;
    }
  };

  const handleBookingAction = async (bookingId, action, data = {}) => {
    try {
      if (action === 'updateStatus') {
        await api.put(`/api/unified-bookings/${bookingId}/status`, data);
      } else if (action === 'cancel') {
        await api.put(`/api/unified-bookings/${bookingId}/status`, { status: 'cancelled', ...data });
      }
      
      fetchBookings();
      if (onBookingChanged) onBookingChanged();
      setShowBookingDialog(false);
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to update booking';
      setError(msg);
    }
  };

  const populateEditBookingFields = (booking) => {
    setEditLocationType(booking?.location?.type || 'on_site');
    setEditLocationAddress(booking?.location?.address || '');
    setEditVideoLink(booking?.location?.videoLink || '');
    setEditSupplierNotes(booking?.supplierNotes || '');
    setEditBookingError('');
  };

  const bookingHasCoupleContact = (booking) => {
    if (!booking) return false;
    const manualEmail = booking.manualBookingDetails?.clientEmail;
    const clientEmail = booking.client?.email;
    return Boolean((manualEmail || clientEmail || '').trim());
  };

  const handleEditTeamMembers = async () => {
    if (!selectedBooking) return;
    
    try {
      // Fetch available team members for the booking time slot
      const response = await api.get(
        `/api/unified-bookings/available-team-members/${selectedBooking.serviceType?._id || selectedBooking.serviceType}?startTime=${selectedBooking.startTime}&endTime=${selectedBooking.endTime}&businessId=${selectedBooking.business?._id || selectedBooking.business}`
      );
      
      setAvailableTeamMembersForEdit(response.data.availableTeamMembers || []);
      
      // Set currently assigned team members as selected
      const currentMemberIds = selectedBooking.assignedTeamMembers?.map(member => {
        const memberId = member.member?._id || member.member;
        return memberId?.toString ? memberId.toString() : memberId;
      }) || [];
      setSelectedTeamMembersForEdit(currentMemberIds);
      populateEditBookingFields(selectedBooking);
      
      setEditTeamMembersOpen(true);
    } catch (error) {
      setError('Failed to load available team members');
    }
  };

  const handleSaveBookingEdit = async (notifyClient = false) => {
    if (!selectedBooking) return;

    const trimmedVideoLink = (editVideoLink || '').trim();
    if (trimmedVideoLink && !/^https?:\/\//i.test(trimmedVideoLink)) {
      setEditBookingError('Enter a full video call URL starting with http:// or https://.');
      return;
    }

    setEditBookingError('');
    setEditBookingSaving(true);

    const teamMemberPayload = selectedTeamMembersForEdit.length > 0
      ? { assignedTeamMembers: selectedTeamMembersForEdit }
      : {};

    try {
      const res = await api.put(`/api/unified-bookings/${selectedBooking._id}`, {
        ...teamMemberPayload,
        supplierNotes: editSupplierNotes,
        location: {
          ...(selectedBooking.location || {}),
          type: editLocationType,
          address: editLocationAddress,
          videoLink: editLocationType === 'virtual' ? trimmedVideoLink : (selectedBooking.location?.videoLink || '')
        },
        notifyClient
      });

      if (res.data?.booking) {
        setSelectedBooking(res.data.booking);
      }
      setEditTeamMembersOpen(false);
      setShowBookingDialog(false);
      if (notifyClient && res.data?.clientNotified) {
        setEditBookingToast({ open: true, message: 'Saved. The couple has been notified by email and in-app.' });
      } else if (notifyClient) {
        setEditBookingToast({ open: true, message: 'Saved. No updates were sent to the couple.' });
      } else {
        setEditBookingToast({ open: true, message: 'Booking saved.' });
      }
      try {
        await fetchBookings();
      } catch (_refreshError) {
        // Saving succeeded; ignore refresh errors so modal close/toast still happen.
      }
    } catch (error) {
      setEditBookingError(error.response?.data?.message || 'Failed to update booking.');
    } finally {
      setEditBookingSaving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return theme.palette.success.main;
      case 'pending': return theme.palette.warning.main;
      case 'rejected': return theme.palette.error.main;
      case 'cancelled': return theme.palette.grey[500];
      case 'completed': return theme.palette.info.main;
      case 'no_show': return theme.palette.error.main;
      case 'rescheduled': return theme.palette.secondary.main;
      case 'awaiting_approval': return theme.palette.warning.main;
      default: return theme.palette.grey[600];
    }
  };

  // Convert bookings to FullCalendar events format
  const bookingEvents = useMemo(() => {
    // Filter out cancelled bookings if showCancelled is false
    let filteredBookings = showCancelled 
      ? bookings 
      : bookings.filter(booking => booking.status !== 'cancelled');

    // Filter by appointment type if a type filter is active
    if (typeFilters.length > 0) {
      filteredBookings = filteredBookings.filter((booking) => typeFilters.includes(getBookingTypeFilterKey(booking)));
    }

    // Filter by visibleUsers if specified
    const visibleUsers = calendarSettings?.visibleUsers || [];
    // Only filter if visibleUsers is set and not all users are selected
    if (visibleUsers.length > 0 && teamMembers.length > 0 && visibleUsers.length < teamMembers.length) {
      filteredBookings = filteredBookings.filter(booking => {
        const assignedMembers = booking.assignedTeamMembers || [];
        // If booking has no assigned members (unassigned), always show it
        if (assignedMembers.length === 0) {
          return true; // Show unassigned bookings even when filtering
        }
        // Check if any assigned member is in the visibleUsers list
        return assignedMembers.some(assigned => {
          const memberId = assigned.member?._id || assigned.member;
          if (!memberId) return false;
          return visibleUsers.some(visibleId => 
            memberId.toString() === visibleId.toString()
          );
        });
      });
    }
    
    return filteredBookings.map(booking => {
    // Ensure dates are properly formatted for FullCalendar
    // FullCalendar accepts ISO strings, Date objects, or properly formatted date strings
    let startDate = booking.startTime;
    let endDate = booking.endTime;
    
    // If dates are Date objects, convert to ISO string
    // If they're already strings (ISO format), keep them as-is
    if (startDate instanceof Date) {
      startDate = startDate.toISOString();
    } else if (startDate && typeof startDate === 'string' && !startDate.includes('T')) {
      // If it's a date string without time info, ensure it's properly formatted
      startDate = new Date(startDate).toISOString();
    }
    
    if (endDate instanceof Date) {
      endDate = endDate.toISOString();
    } else if (endDate && typeof endDate === 'string' && !endDate.includes('T')) {
      // If it's a date string without time info, ensure it's properly formatted
      endDate = new Date(endDate).toISOString();
    }
    
    // Check if booking has no assigned team members
    const assignedMembers = booking.assignedTeamMembers || [];
    const isUnassigned = assignedMembers.length === 0;
    
    // Use different styling for unassigned bookings
    const baseColor = booking.status === 'cancelled'
      ? getStatusColor(booking.status)
      : (booking.serviceType?.color || getStatusColor(booking.status));
    const backgroundColor = isUnassigned ? '#fff3cd' : baseColor; // Light yellow/amber for unassigned
    const borderColor = isUnassigned ? '#ffc107' : baseColor; // Amber border for unassigned
    const borderWidth = isUnassigned ? 2 : 2;
    const _borderStyle = isUnassigned ? 'dashed' : 'solid'; // Dashed border for unassigned
    
    return {
      id: `booking-${booking._id}`,
      title: isUnassigned 
        ? `${booking.serviceType?.name || 'Appointment'} (Unassigned)`
        : (booking.serviceType?.name || 'Appointment'),
      start: startDate,
      end: endDate,
      allDay: false, // Explicitly mark as timed event (not all-day) for month view
      backgroundColor: backgroundColor,
      borderColor: borderColor,
      borderWidth: borderWidth,
      extendedProps: {
        booking: booking,
        status: booking.status,
        client: booking.client,
        serviceType: booking.serviceType,
        type: 'booking',
        isUnassigned: isUnassigned
      },
      classNames: isUnassigned ? ['fc-event-unassigned'] : []
    };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [bookings, theme, showCancelled, calendarSettings?.visibleUsers, teamMembers.length, typeFilters]);

  // Convert events to FullCalendar events format with visual distinction
  const eventCalendarEvents = useMemo(() => {
    // Filter out cancelled events if showCancelled is false
    let filteredEvents = showCancelled 
      ? events 
      : events.filter(event => event.status !== 'cancelled' && !event.isCancelled);

    // Filter by event tag if a type filter is active
    if (typeFilters.length > 0) {
      filteredEvents = filteredEvents.filter((event) => typeFilters.includes(getEventTypeFilterKey(event)));
    }

    // Check if we're in a time grid view (week or day) where all-day events need duration
    const isTimeGridView = view === 'week' || view === 'day';
    
    return filteredEvents.map(event => {
    // Ensure dates are properly formatted for FullCalendar
    // FullCalendar accepts ISO strings, Date objects, or properly formatted date strings
    let startDate = event.startTime;
    let endDate = event.endTime;
    
    // If dates are Date objects, convert to ISO string
    // If they're already strings (ISO format), keep them as-is
    if (startDate instanceof Date) {
      startDate = startDate.toISOString();
    } else if (startDate && typeof startDate === 'string' && !startDate.includes('T')) {
      // If it's a date string without time info, ensure it's properly formatted
      startDate = new Date(startDate).toISOString();
    }
    
    if (endDate instanceof Date) {
      endDate = endDate.toISOString();
    } else if (endDate && typeof endDate === 'string' && !endDate.includes('T')) {
      // If it's a date string without time info, ensure it's properly formatted
      endDate = new Date(endDate).toISOString();
    }
    
    const isCancelled = event.status === 'cancelled' || event.isCancelled;
    
    // Determine if event is all-day
    let isAllDay = false;
    if (event.allDay === true) {
      isAllDay = true;
    } else if (startDate && endDate) {
      // Check if start is at 00:00:00 and end is at 23:59:59 or 00:00:00 next day
      const start = new Date(startDate);
      const end = new Date(endDate);
      const startHour = start.getHours();
      const startMinute = start.getMinutes();
      const startSecond = start.getSeconds();
      const endHour = end.getHours();
      const endMinute = end.getMinutes();
      const endSecond = end.getSeconds();
      
      // Check if it's exactly a full day (start at 00:00:00, end at 23:59:59 or next day 00:00:00)
      const isFullDay = (startHour === 0 && startMinute === 0 && startSecond === 0) &&
        ((endHour === 23 && endMinute === 59 && endSecond === 59) ||
         (endHour === 0 && endMinute === 0 && endSecond === 0 && end.getDate() === start.getDate() + 1));
      
      isAllDay = isFullDay;
    }
    
    // For time grid views (week/day), convert all-day events to timed events with 12-hour duration
    let displayStartDate = startDate;
    let displayEndDate = endDate;
    let displayAllDay = isAllDay;
    
    if (isAllDay && isTimeGridView && startDate) {
      // Convert all-day event to a 12-hour timed event for display in time grid views
      const start = new Date(startDate);
      // Set start time to 9:00 AM
      start.setHours(9, 0, 0, 0);
      displayStartDate = start.toISOString();
      
      // Set end time to 12 hours later (9:00 PM)
      const end = new Date(start);
      end.setHours(21, 0, 0, 0);
      displayEndDate = end.toISOString();
      
      // Mark as not all-day for time grid views so it displays in the time slots
      displayAllDay = false;
    }

    // Respect saved colour from the form; only fall back to blocker/simultaneous defaults when absent
    const defaultBlockBackground = '#f44336';
    const defaultBlockBorder = '#d32f2f';
    const defaultSimultaneousBackground = '#ff9800';
    const defaultSimultaneousBorder = '#f57c00';
    const fillColor = isCancelled
      ? '#9e9e9e'
      : event.color ||
        (event.isBlocker ? defaultBlockBackground : defaultSimultaneousBackground);
    const strokeColor = isCancelled
      ? '#757575'
      : event.color ||
        (event.isBlocker ? defaultBlockBorder : defaultSimultaneousBorder);

    return {
      id: `event-${event._id}`,
      title: event.title,
      start: displayStartDate,
      end: displayEndDate,
      allDay: displayAllDay,
      backgroundColor: fillColor,
      borderColor: strokeColor,
      borderWidth: event.isBlocker ? 3 : 2,
      display: event.isBlocker ? 'block' : 'block',
      extendedProps: {
        event: event,
        type: 'event',
        isBlocker: event.isBlocker,
        eventType: event.eventType,
        isCancelled,
        allDay: isAllDay // Keep original allDay value in extendedProps
      },
      classNames: [
        ...(event.isBlocker ? ['fc-event-blocking'] : ['fc-event-simultaneous']),
        ...(isCancelled ? ['fc-event-cancelled'] : [])
      ]
    };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [events, showCancelled, view, typeFilters]);

  const openBookingDetails = useCallback((booking) => {
    if (!booking) return;
    if (onBookingClick) {
      onBookingClick(booking);
      return;
    }
    setSelectedBooking(booking);
    setShowBookingDialog(true);
  }, [onBookingClick]);

  const listViewGroups = useMemo(() => {
    const now = new Date();
    const weekStartsOn = calendarSettings?.startOfWeek ?? 1;
    const todayStart = startOfDay(now);
    const tomorrowStart = startOfDay(addDays(todayStart, 1));
    const weekEnd = endOfDay(endOfWeek(now, { weekStartsOn }));
    const monthEnd = endOfDay(endOfMonth(now));
    const yearEnd = endOfDay(new Date(now.getFullYear(), 11, 31));

    const initialGroups = [
      { key: 'today', label: 'Today', items: [] },
      { key: 'thisWeek', label: 'This week', items: [] },
      { key: 'thisMonth', label: 'This month', items: [] },
      { key: 'thisYear', label: 'This year', items: [] },
      { key: 'nextYearOnwards', label: 'Next year and onwards', items: [] }
    ];

    const sourceBookings = (showCancelled
      ? listViewBookings
      : listViewBookings.filter((booking) => booking.status !== 'cancelled'))
      .filter((booking) => {
        const bookingDate = new Date(booking.startTime);
        return !Number.isNaN(bookingDate.getTime()) && bookingDate >= todayStart;
      })
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

    const sourceEvents = (showCancelled
      ? listViewEvents
      : listViewEvents.filter((event) => event.status !== 'cancelled' && !event.isCancelled))
      .filter((event) => {
        const eventDate = new Date(event.startTime);
        return !Number.isNaN(eventDate.getTime()) && eventDate >= todayStart;
      });

    const sourceItems = [
      ...sourceBookings.map((booking) => ({
        id: booking._id,
        type: 'booking',
        startTime: booking.startTime,
        endTime: booking.endTime,
        status: booking.status,
        booking
      })),
      ...sourceEvents.map((event) => ({
        id: event._id,
        type: 'event',
        startTime: event.startTime,
        endTime: event.endTime,
        status: event.status,
        event
      }))
    ].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

    sourceItems.forEach((item) => {
      const itemDate = new Date(item.startTime);
      if (itemDate < tomorrowStart) {
        initialGroups[0].items.push(item);
      } else if (itemDate <= weekEnd) {
        initialGroups[1].items.push(item);
      } else if (itemDate <= monthEnd) {
        initialGroups[2].items.push(item);
      } else if (itemDate <= yearEnd) {
        initialGroups[3].items.push(item);
      } else {
        initialGroups[4].items.push(item);
      }
    });

    return initialGroups;
  }, [calendarSettings?.startOfWeek, listViewBookings, listViewEvents, showCancelled]);

  const calendarEvents = useMemo(() => [...bookingEvents, ...eventCalendarEvents], [bookingEvents, eventCalendarEvents]);

  const getCalendarView = () => {
    switch (view) {
      case 'month': return 'dayGridMonth';
      case 'week': return 'timeGridWeek';
      case 'day': return 'timeGridDay';
      default: return 'timeGridDay'; // Default to day view
    }
  };

  const getOrdinal = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  };

  // Week title from FullCalendar's dateRange so it matches the grid (start is first day, end is exclusive)
  const getWeekTitleFromDateRange = () => {
    if (!dateRange?.start || !dateRange?.end || dateRange.view !== 'timeGridWeek') return null;
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    end.setDate(end.getDate() - 1);
    const startDay = start.getDate();
    const endDay = end.getDate();
    const startMonth = start.toLocaleDateString('en-US', { month: 'long' });
    const endMonth = end.toLocaleDateString('en-US', { month: 'long' });
    if (startMonth === endMonth) {
      return `${startMonth} ${startDay}${getOrdinal(startDay)} - ${endDay}${getOrdinal(endDay)}`;
    }
    return `${startMonth} ${startDay}${getOrdinal(startDay)} - ${endMonth} ${endDay}${getOrdinal(endDay)}`;
  };

  // Format date based on view (day, week, month)
  const formatDateWithOrdinal = (date, currentView) => {
    if (currentView === 'day') {
      const day = date.getDate();
      const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
      const month = date.toLocaleDateString('en-US', { month: 'long' });
      const dayWithOrdinal = `${day}${getOrdinal(day)}`;
      return `${weekday} ${dayWithOrdinal} ${month}`;
    } else if (currentView === 'week') {
      const startOfWeek = new Date(date);
      const day = startOfWeek.getDay();
      const dayOfWeek = day === 0 ? 7 : day;
      const diff = startOfWeek.getDate() - dayOfWeek + 1;
      startOfWeek.setDate(diff);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      const startDay = startOfWeek.getDate();
      const endDay = endOfWeek.getDate();
      const startMonth = startOfWeek.toLocaleDateString('en-US', { month: 'long' });
      const endMonth = endOfWeek.toLocaleDateString('en-US', { month: 'long' });
      if (startMonth === endMonth) {
        return `${startMonth} ${startDay}${getOrdinal(startDay)} - ${endDay}${getOrdinal(endDay)}`;
      }
      return `${startMonth} ${startDay}${getOrdinal(startDay)} - ${endMonth} ${endDay}${getOrdinal(endDay)}`;
    } else if (currentView === 'month') {
      const month = date.toLocaleDateString('en-US', { month: 'long' });
      const year = date.getFullYear();
      return `${month} ${year}`;
    }
    
    // Fallback
    const day = date.getDate();
    const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const dayWithOrdinal = `${day}${getOrdinal(day)}`;
    return `${weekday} ${dayWithOrdinal} ${month}`;
  };

  const handleEventClick = useCallback((info) => {
    const { type, booking, event: evt } = info.event.extendedProps || {};
    if (type === 'event' && evt?._id) {
      if (onEventClick) {
        onEventClick(evt);
      }
      return;
    }
    if (booking) {
      // If onBookingClick is provided, use it to fetch fresh data
      // Otherwise, use the local dialog
      if (onBookingClick) {
        onBookingClick(booking);
      } else {
        setSelectedBooking(booking);
        setShowBookingDialog(true);
      }
    }
  }, [onEventClick, onBookingClick]);

  const renderCalendarEventContent = useCallback((eventInfo) => {
    const { booking, event: calendarEvent, type } = eventInfo.event.extendedProps || {};
    const isBooking = type === 'booking' && booking;
    const people = isBooking
      ? getBookingDisplayPeople(booking)
      : (type === 'event' && calendarEvent ? getEventDisplayPeople(calendarEvent) : []);
    const eventStart = eventInfo.event?.start ? new Date(eventInfo.event.start) : null;
    const eventEnd = eventInfo.event?.end ? new Date(eventInfo.event.end) : null;
    const durationMinutes = (eventStart && eventEnd)
      ? Math.max(0, Math.round((eventEnd.getTime() - eventStart.getTime()) / 60000))
      : 60;
    const viewType = eventInfo.view?.type;
    const isMonthView = viewType === 'dayGridMonth';
    const isWeekView = viewType === 'timeGridWeek';
    const guestName = isBooking ? getBookingGuestDisplayName(booking) : '';
    const timeLabel = isBooking && eventStart ? format(eventStart, 'h:mm a') : '';
    // When unassigned, the people list already shows the couple, so avoid duplicating the name
    const hasAssignedStaff = isBooking && (booking.assignedTeamMembers || []).length > 0;
    // Duration-based truncation only applies to time grids, where chip height tracks
    // duration. Month chips always have room for content.
    const hideAllContent = !isMonthView && durationMinutes < 15;
    const showTitleOnly = !isMonthView && durationMinutes >= 15 && durationMinutes < 20;
    if (hideAllContent) {
      return <Box sx={{ height: '100%' }} />;
    }
    if (showTitleOnly) {
      return (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            width: '100%',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: 700,
            lineHeight: 1.1
          }}
          noWrap
        >
          {timeLabel ? `${timeLabel} · ${eventInfo.event.title}` : eventInfo.event.title}
        </Typography>
      );
    }

    const hasTightSpace = isMonthView || (isWeekView && durationMinutes <= 45);
    const visiblePeople = people.slice(0, hasTightSpace ? 3 : 2);
    const hiddenCount = Math.max(people.length - visiblePeople.length, 0);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        {timeLabel && (isMonthView || isWeekView) && (
          <Typography variant="caption" sx={{ lineHeight: 1.1, opacity: 0.85, fontWeight: 600 }} noWrap>
            {timeLabel}
          </Typography>
        )}
        <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.1 }} noWrap>
          {eventInfo.event.title}
        </Typography>
        {guestName && (isMonthView || (!hasTightSpace && hasAssignedStaff)) && (
          <Typography variant="caption" sx={{ lineHeight: 1.1, opacity: 0.9 }} noWrap>
            {guestName}
          </Typography>
        )}
        <Box
          sx={{
            mt: 'auto',
            mb: 0.25,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.25,
            minWidth: 0,
            overflow: 'hidden'
          }}
        >
          {hasTightSpace ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
              {visiblePeople.map((person, index) => (
                <Avatar
                  key={`${person.name}-${index}`}
                  src={person.avatar}
                  sx={{ width: 16, height: 16, fontSize: '0.6rem', flexShrink: 0 }}
                >
                  {(person.name || 'U').charAt(0)}
                </Avatar>
              ))}
              {!isMonthView && guestName && (
                <Typography variant="caption" sx={{ lineHeight: 1.1, opacity: 0.9, minWidth: 0 }} noWrap>
                  {guestName}
                </Typography>
              )}
            </Box>
          ) : (
            visiblePeople.map((person, index) => (
              <Box key={`${person.name}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                <Avatar src={person.avatar} sx={{ width: 16, height: 16, fontSize: '0.6rem', flexShrink: 0 }}>
                  {(person.name || 'U').charAt(0)}
                </Avatar>
                <Typography
                  variant="caption"
                  sx={{ lineHeight: 1.1, opacity: 0.9, minWidth: 0 }}
                  noWrap
                >
                  {person.name || 'Unnamed'}
                </Typography>
              </Box>
            ))
          )}
          {hiddenCount > 0 && (
            <Typography variant="caption" sx={{ lineHeight: 1.1, opacity: 0.8 }} noWrap>
              +{hiddenCount} more
            </Typography>
          )}
        </Box>
      </Box>
    );
  }, [getBookingDisplayPeople, getEventDisplayPeople]);

  const getCalendarEventTooltipText = useCallback((fcEvent) => {
    const { booking, event: calendarEvent, type } = fcEvent.extendedProps || {};
    const isBooking = type === 'booking' && booking;
    const people = isBooking
      ? getBookingDisplayPeople(booking)
      : (type === 'event' && calendarEvent ? getEventDisplayPeople(calendarEvent) : []);
    const assigneeNames = people
      .map((person) => person?.name)
      .filter((name) => Boolean(name && name.trim()));
    const assigneeLabel = assigneeNames.length > 0 ? assigneeNames.join(', ') : 'Unassigned';
    const guestName = isBooking ? getBookingGuestDisplayName(booking) : '';
    const baseTitle = fcEvent.title || 'Untitled';
    const titleWithGuest = guestName ? `${baseTitle} with ${guestName}` : baseTitle;
    const startTime = fcEvent.start ? format(new Date(fcEvent.start), 'h:mm a') : '';
    return {
      title: startTime && isBooking ? `${startTime} · ${titleWithGuest}` : titleWithGuest,
      assignees: assigneeLabel
    };
  }, [getBookingDisplayPeople, getEventDisplayPeople]);

  const getInlineCalendarItemTooltipText = useCallback((item) => {
    const isBooking = Boolean(item?.serviceType);
    const guestName = isBooking ? getBookingGuestDisplayName(item) : '';
    const baseTitle = isBooking
      ? (item?.serviceType?.name || 'Appointment')
      : (item?.title || 'Event');
    const title = guestName ? `${baseTitle} with ${guestName}` : baseTitle;
    const people = isBooking ? getBookingDisplayPeople(item) : getEventDisplayPeople(item);
    const assigneeNames = people
      .map((person) => person?.name)
      .filter((name) => Boolean(name && name.trim()));
    const assigneeLabel = assigneeNames.length > 0 ? assigneeNames.join(', ') : 'Unassigned';
    return `${title}\nAssignee${assigneeNames.length === 1 ? '' : 's'}: ${assigneeLabel}`;
  }, [getBookingDisplayPeople, getEventDisplayPeople]);

  const handleCalendarEventMouseEnter = useCallback((info) => {
    const tooltipData = getCalendarEventTooltipText(info.event);
    setEventTooltip({
      open: true,
      anchorEl: info.el,
      title: tooltipData.title,
      assignees: tooltipData.assignees
    });
  }, [getCalendarEventTooltipText]);

  const handleCalendarEventMouseLeave = useCallback(() => {
    setEventTooltip((prev) => ({ ...prev, open: false, anchorEl: null }));
  }, []);

  // Check if the business is open for a given day.
  const hasShiftsForDay = useCallback((date) => {
    if (!openingHours) return true;

    const dayHours = getOpeningHoursForDate(date);
    return Boolean(dayHours?.isOpen && !isClosedDate(date));
  }, [openingHours, getOpeningHoursForDate, isClosedDate]);

  // Get staff members available in a specific time slot
  const _getStaffForTimeSlot = useCallback((slotDate) => {
    if (!teamMemberShifts || !teamMembers || teamMemberShifts.length === 0 || teamMembers.length === 0) return [];
    
    const slotEnd = new Date(slotDate);
    slotEnd.setMinutes(slotEnd.getMinutes() + 30);
    const slotDay = new Date(slotDate);
    slotDay.setHours(0, 0, 0, 0);
    
    const availableStaff = [];
    teamMemberShifts.forEach(shift => {
      if (!shift || !shift.startTime || !shift.endTime) return;
      
      const shiftStart = new Date(shift.startTime);
      const shiftEnd = new Date(shift.endTime);
      const shiftDay = new Date(shiftStart);
      shiftDay.setHours(0, 0, 0, 0);
      
      // Only check shifts on the same day
      if (shiftDay.getTime() !== slotDay.getTime()) return;
      
      // Check if shift overlaps with this slot
      const slotStartTime = slotDate.getTime();
      const slotEndTime = slotEnd.getTime();
      const shiftStartTime = shiftStart.getTime();
      const shiftEndTime = shiftEnd.getTime();
      
      if (shiftStartTime < slotEndTime && shiftEndTime > slotStartTime) {
        const staffId = (shift.teamMember?._id || shift.teamMember)?.toString();
        const member = teamMembers.find(m => 
          (m.staffMemberId || m._id)?.toString() === staffId
        );
        if (member && !availableStaff.find(s => s._id === member._id)) {
          availableStaff.push(member);
        }
      }
    });
    
    return availableStaff;
  }, [teamMemberShifts, teamMembers]);

  // Get staff members working on a specific day
  const getStaffForDay = useCallback((date) => {
    if (!teamMemberShifts || !teamMembers || teamMemberShifts.length === 0 || teamMembers.length === 0) return [];
    
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(date);
    dayEnd.setHours(23, 59, 59, 999);
    
    // Get unique staff member IDs that have shifts on this day
    const staffIdsWithShifts = new Set();
    teamMemberShifts.forEach(shift => {
      const shiftStart = new Date(shift.startTime);
      const shiftEnd = new Date(shift.endTime);
      if (shiftStart < dayEnd && shiftEnd > dayStart) {
        const staffId = shift.teamMember?._id || shift.teamMember;
        if (staffId) {
          staffIdsWithShifts.add(staffId.toString());
        }
      }
    });
    
    // Map to team members
    return teamMembers.filter(member => {
      const memberId = (member.staffMemberId || member._id)?.toString();
      return memberId && staffIdsWithShifts.has(memberId);
    });
  }, [teamMemberShifts, teamMembers]);

  const handleDateClick = useCallback((info) => {
    const isDayClick = info.allDay !== false; // Day clicks typically have allDay property

    // Prevent clicking on past dates
    if (isDayClick ? isPastCalendarDay(info.date) : isPastDate(info.date)) {
      return; // Don't allow scheduling in the past
    }
    
      // For suppliers, allow creating bookings from future dates without staff-shift checks.
    if (userType === 'supplier') {
      // Navigate to day view so user can select a specific time slot
      if (isDayClick) {
        // Navigate to day view for the clicked date
        setCurrentDate(info.date);
        setView('day');
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.changeView('timeGridDay', info.date);
        }
        return;
      }
      // For time slot clicks, allow supplier-created bookings without staff shifts.
      
      // For suppliers, clicking a time slot opens the manual booking form with that date/time prefilled
      // Week view: we know date and time, but not staff member
      const clickedDate = info.date;
      const dateStr = clickedDate.toISOString().split('T')[0]; // Format: YYYY-MM-DD
      const timeStr = clickedDate.toTimeString().split(' ')[0].substring(0, 5); // Format: HH:MM
      navigate(`/supplier/bookings/new?date=${dateStr}&time=${timeStr}`);
    } else {
      // For clients, navigate to day view
      setCurrentDate(info.date);
      if (view === 'month') {
        setView('day');
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.changeView('timeGridDay', info.date);
        }
      }
    }
  }, [userType, navigate, view, isPastDate, isPastCalendarDay, setView, setCurrentDate]);

  const handleSelect = useCallback((selectInfo) => {
    // Prevent selecting past dates
    if (isPastDate(selectInfo.start)) {
      selectInfo.view.calendar.unselect(); // Clear the selection
      return; // Don't allow scheduling in the past
    }
    
    if (userType === 'supplier') {
      // For suppliers, selecting a time range opens the manual booking form
      // Week view: we know date and time, but not staff member
      const startDate = selectInfo.start;
      const dateStr = startDate.toISOString().split('T')[0]; // Format: YYYY-MM-DD
      const timeStr = startDate.toTimeString().split(' ')[0].substring(0, 5); // Format: HH:MM
      
      // Clear the selection immediately to prevent the mirror from staying
      selectInfo.view.calendar.unselect();
      
      navigate(`/supplier/bookings/new?date=${dateStr}&time=${timeStr}`);
    }
  }, [userType, navigate, isPastDate]);

  // Memoize datesSet callback to prevent infinite re-renders
  const handleDatesSet = useCallback((dateInfo) => {
    // Store the full date range from FullCalendar for accurate month view queries
    // Use ref to avoid triggering infinite loops
    if (dateInfo.view.type === 'dayGridMonth') {
      const prevRange = fullCalendarDateRangeRef.current;
      const rangeChanged = !prevRange ||
        prevRange.start.getTime() !== dateInfo.start.getTime() ||
        prevRange.end.getTime() !== dateInfo.end.getTime();
      fullCalendarDateRangeRef.current = {
        start: dateInfo.start,
        end: dateInfo.end
      };
      if (rangeChanged) {
        // Refetch with FullCalendar's actual visible grid range. The first fetch can run
        // before this callback fires (using an estimated range), so edge-of-month days
        // would otherwise be missing until the user navigates away and back.
        setFcMonthRangeVersion(prev => prev + 1);
      }
    } else {
      fullCalendarDateRangeRef.current = null;
    }
    
    // Only update dateRange state if it actually changed to prevent infinite loops
    setDateRange(prev => {
      if (!prev || 
          prev.start?.getTime() !== dateInfo.start.getTime() || 
          prev.end?.getTime() !== dateInfo.end.getTime() ||
          prev.view !== dateInfo.view.type) {
        return {
          start: dateInfo.start,
          end: dateInfo.end,
          view: dateInfo.view.type
        };
      }
      return prev; // Return same reference if nothing changed
    });
    
    // For month view, use the actual month (first day of the month) instead of the first visible date
    // which might be from the previous month
    let newDate = dateInfo.start;
    if (dateInfo.view.type === 'dayGridMonth') {
      // FullCalendar's start for month view is the first day of the week containing the first day of the month
      // The simplest way to find the actual month is to use the middle of the visible range
      // The middle will always be in the actual month being displayed
      const viewStart = new Date(dateInfo.start);
      const viewEnd = new Date(dateInfo.end);
      
      // Calculate the middle of the visible range
      const timeDiff = viewEnd.getTime() - viewStart.getTime();
      const middleTime = viewStart.getTime() + (timeDiff / 2);
      const middleDate = new Date(middleTime);
      
      // Use the month and year from the middle date
      const targetMonth = middleDate.getMonth();
      const targetYear = middleDate.getFullYear();
      
      // Create the first day of the target month
      const firstOfMonth = new Date(targetYear, targetMonth, 1);
      newDate = firstOfMonth;
    }
    
    // Avoid infinite loops by only updating when date actually changes
    // Compare months for month view, exact dates for other views
    const shouldUpdate = dateInfo.view.type === 'dayGridMonth' 
      ? (!currentDateRef.current || 
         currentDateRef.current.getFullYear() !== newDate.getFullYear() ||
         currentDateRef.current.getMonth() !== newDate.getMonth())
      : (!currentDateRef.current || currentDateRef.current.getTime() !== newDate.getTime());
    
    if (shouldUpdate) {
      setCurrentDate(newDate);
    }
    
    // Fetch shifts for staff display when the view/date changes.
    if (businessId && userType === 'supplier') {
      const viewType = dateInfo.view.type;
      if (viewType === 'timeGridDay') {
        // Day view - fetch staff context for the current day
        const dayStart = new Date(dateInfo.start);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(dateInfo.start);
        dayEnd.setHours(23, 59, 59, 999);
        api.get(`/api/team-member-shifts/business/${businessId}`, {
          params: {
            start: dayStart.toISOString(),
            end: dayEnd.toISOString()
          }
        }).then(response => {
          const shifts = response.data.shifts || [];
          setTeamMemberShifts(shifts);
        }).catch(_error => {
          setTeamMemberShifts([]);
        });
      } else if (viewType === 'timeGridWeek') {
        // Week view - fetch staff context for the visible week
        api.get(`/api/team-member-shifts/business/${businessId}`, {
          params: {
            start: dateInfo.start.toISOString(),
            end: dateInfo.end.toISOString()
          }
        }).then(response => {
          const shifts = response.data.shifts || [];
          setTeamMemberShifts(shifts);
        }).catch(_error => {
          setTeamMemberShifts([]);
        });
      }
    }
    
    // Trigger disabled slot application immediately when calendar renders for week view
    if (userType === 'supplier' && dateInfo.view.type === 'timeGridWeek') {
      // Trigger the useEffect immediately and also after a short delay to catch any late-rendering elements
      setCalendarRenderKey(prev => prev + 1);
      setTimeout(() => {
        setCalendarRenderKey(prev => prev + 1);
      }, 300);
    }
    
    // Note: Removed setView calls from here to prevent infinite loops
    // FullCalendar manages view internally, and calling setView here causes
    // handleDatesSet to be recreated (due to view dependency), which triggers
    // datesSet again, creating an infinite loop
  }, [businessId, userType]);

  // Note: Removed useEffect that injected avatars into time slots - avatars are now in day headers for week view

  const _renderMonthView = () => {
    const startOfMonth = getStartOfPeriod(currentDate, 'month');
    const _endOfMonth = getEndOfPeriod(currentDate, 'month');
    const startOfCalendar = new Date(startOfMonth);
    startOfCalendar.setDate(startOfCalendar.getDate() - startOfMonth.getDay());
    
    const days = [];
    const currentDay = new Date(startOfCalendar);
    
    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDay));
      currentDay.setDate(currentDay.getDate() + 1);
    }

    const dayBookings = (date) => {
      return bookings.filter(booking => {
        const bookingDate = new Date(booking.startTime);
        return bookingDate.toDateString() === date.toDateString();
      });
    };

    return (
      <Grid container spacing={1}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <Grid item xs key={day}>
            <Typography variant="subtitle2" align="center" sx={{ p: 1, fontWeight: 'bold' }}>
              {day}
            </Typography>
          </Grid>
        ))}
        
        {days.map((day, index) => {
          const dayBookingsList = dayBookings(day);
          const isCurrentMonth = day.getMonth() === currentDate.getMonth();
          const isToday = day.toDateString() === new Date().toDateString();
          const isPast = isPastCalendarDay(day);
          const isOpen = hasShiftsForDay(day);
          const isClickable = !isPast; // Suppliers can create appointments even outside opening hours.
          const isDisabled = isPast || (userType === 'supplier' && !isOpen);
          
          const dayBox = (
            <Box
              sx={{
                minHeight: 100,
                p: 1,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: isDisabled 
                  ? 'action.hover' 
                  : (isCurrentMonth ? 'background.paper' : 'background.default'),
                opacity: isDisabled ? 0.65 : (isCurrentMonth ? 1 : 0.5),
                cursor: isPast ? 'not-allowed' : 'pointer',
                position: 'relative',
                '&::before': isDisabled ? {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  pointerEvents: 'none',
                  zIndex: 1
                } : {}
              }}
              onClick={() => {
                // Prevent clicking on past days only.
                if (isPast) {
                  return;
                }
                if (isClickable) {
                  if (userType === 'supplier') {
                    // Month view: we only know the date - navigate to booking form with date prefilled
                    const dateStr = day.toISOString().split('T')[0]; // Format: YYYY-MM-DD
                    navigate(`/supplier/bookings/new?date=${dateStr}`);
                  } else {
                    // For clients, navigate to day view
                    setCurrentDate(day);
                    setView('day');
                  }
                }
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: isToday ? 'bold' : 'normal',
                  color: isDisabled 
                    ? 'text.disabled' 
                    : (isToday ? 'primary.main' : 'text.primary'),
                  position: 'relative',
                  zIndex: 2
                }}
              >
                {day.getDate()}
              </Typography>
              
              {/* Show staff avatars for this day (supplier view only) */}
              {userType === 'supplier' && !isPast && (() => {
                let staffForDay = getStaffForDay(day);
                
                // Filter by visibleUsers if specified
                const visibleUsers = calendarSettings?.visibleUsers || [];
                if (visibleUsers.length > 0 && teamMembers.length > 0 && visibleUsers.length < teamMembers.length) {
                  staffForDay = staffForDay.filter(member => {
                    const memberId = member._id || member.businessMember?._id;
                    return memberId && visibleUsers.some(visibleId => 
                      memberId.toString() === visibleId.toString()
                    );
                  });
                }
                
                if (staffForDay.length > 0) {
                  return (
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', mt: 0.5, mb: 0.5, flexWrap: 'wrap', position: 'relative', zIndex: 2 }}>
                      {staffForDay.slice(0, 3).map((member, idx) => {
                        const memberName = getMemberDisplayName(member);
                        return (
                          <Avatar
                            key={member._id || idx}
                            src={getAvatarSrc(getStaffMemberDisplayPhoto(member))}
                            sx={{ 
                              width: 20, 
                              height: 20, 
                              border: '1px solid',
                              borderColor: 'primary.main',
                              fontSize: '0.6rem'
                            }}
                            alt={memberName}
                          >
                            {memberName.charAt(0)}
                          </Avatar>
                        );
                      })}
                      {staffForDay.length > 3 && (
                        <Avatar
                          sx={{ 
                            width: 20, 
                            height: 20, 
                            bgcolor: 'rgba(0,0,0,0.1)',
                            fontSize: '0.6rem',
                            color: 'text.secondary'
                          }}
                        >
                          +{staffForDay.length - 3}
                        </Avatar>
                      )}
                    </Box>
                  );
                }
                return null;
              })()}
              
              {dayBookingsList.slice(0, 3).map((booking, idx) => (
                <Chip
                  key={idx}
                  label={booking.serviceType?.name || 'Service'}
                  size="small"
                  color={getStatusColorForChip(booking.status)}
                  sx={{ 
                    fontSize: '0.7rem',
                    height: 20,
                    mb: 0.5,
                    display: 'block',
                    position: 'relative',
                    zIndex: 2,
                    '& .MuiChip-label': {
                      px: 1
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedBooking(booking);
                    setShowBookingDialog(true);
                  }}
                />
              ))}
              
              {dayBookingsList.length > 3 && (
                <Typography variant="caption" color="text.secondary" sx={{ position: 'relative', zIndex: 2 }}>
                  +{dayBookingsList.length - 3} more
                </Typography>
              )}
            </Box>
          );
          
            const tooltipTitle = isPast 
              ? "Cannot schedule in the past" 
              : (isDisabled ? "Business closed" : "");
            
            return (
            <Grid item xs key={index}>
              {isDisabled ? (
                <Tooltip title={tooltipTitle} arrow>
                  {dayBox}
                </Tooltip>
              ) : (
                dayBox
              )}
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const _renderWeekView = () => {
    const startOfWeek = getStartOfPeriod(currentDate, 'week');
    const days = [];
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }

    const dayBookings = (date) => {
      return bookings.filter(booking => {
        const bookingDate = new Date(booking.startTime);
        return bookingDate.toDateString() === date.toDateString();
      });
    };

    return (
      <Grid container spacing={1}>
        {days.map((day, index) => {
          const dayBookingsList = dayBookings(day);
          const isToday = day.toDateString() === new Date().toDateString();
          
          return (
            <Grid item xs key={index}>
              <Card sx={{ minHeight: 400 }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    align="center"
                    sx={{
                      fontWeight: isToday ? 'bold' : 'normal',
                      color: isToday ? 'primary.main' : 'text.primary',
                      mb: 2
                    }}
                  >
                    {day.toLocaleDateString('en-US', { weekday: 'short' })}
                    <br />
                    {day.getDate()}
                  </Typography>
                  
                  {dayBookingsList.map((booking, idx) => (
                    <Card
                      key={idx}
                      sx={{
                        mb: 1,
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'action.hover' }
                      }}
                      onClick={() => {
                        setSelectedBooking(booking);
                        setShowBookingDialog(true);
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          {getStatusIcon(booking.status)}
                          <Chip
                            label={booking.status}
                            size="small"
                            color={getStatusColorForChip(booking.status)}
                          />
                        </Box>
                        <Typography variant="subtitle2" gutterBottom>
                          {booking.serviceType?.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(booking.startTime).toLocaleTimeString([], { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </Typography>
                        {userType === 'supplier' && (
                          <Typography variant="body2" color="text.secondary">
                            {getBookingGuestDisplayName(booking)}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const _renderDayView = () => {
    const dayBookings = bookings.filter(booking => {
      const bookingDate = new Date(booking.startTime);
      return bookingDate.toDateString() === currentDate.toDateString();
    });

    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          {currentDate.toLocaleDateString('en-US', { 
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
        </Typography>
        
        <Grid container spacing={2}>
          {dayBookings.map((booking) => (
              <Grid item xs={12} sm={6} md={4} key={booking._id}>
                <Card
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    setSelectedBooking(booking);
                    setShowBookingDialog(true);
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      {getStatusIcon(booking.status)}
                      <Chip
                        label={booking.status}
                        size="small"
                        color={getStatusColorForChip(booking.status)}
                      />
                    </Box>
                    
                    <Typography variant="h6" gutterBottom>
                      {booking.serviceType?.name}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Clock size={16} />
                      <Typography variant="body2">
                        {new Date(booking.startTime).toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })} - {new Date(booking.endTime).toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </Typography>
                    </Box>
                    
                    {userType === 'supplier' && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <User size={16} />
                        <Typography variant="body2">
                          {getBookingGuestDisplayName(booking)}
                        </Typography>
                      </Box>
                    )}
                    
                    {booking.location?.address && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <MapPin size={16} />
                        <Typography variant="body2">
                          {booking.location.address}
                        </Typography>
                      </Box>
                    )}
                    
                    {booking.clientNotes && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ChatCircleText size={16} weight="regular" />
                        <Typography variant="body2" noWrap>
                          {booking.clientNotes}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
        </Grid>
      </Box>
    );
  };

  const renderBookingDialog = () => {
    if (!selectedBooking) return null;

    return (
      <Dialog
        open={showBookingDialog}
        onClose={() => setShowBookingDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getStatusIcon(selectedBooking.status)}
            <Typography variant="h6">
              {selectedBooking.serviceType?.name}
            </Typography>
            <Chip
              label={selectedBooking.status}
              size="small"
              color={getStatusColorForChip(selectedBooking.status)}
            />
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Date & Time
              </Typography>
              <Typography variant="body1" gutterBottom>
                {new Date(selectedBooking.startTime).toLocaleDateString()} at{' '}
                {new Date(selectedBooking.startTime).toLocaleTimeString([], { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </Typography>
              
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Duration
              </Typography>
              <Typography variant="body1" gutterBottom>
                {selectedBooking.serviceType?.duration} minutes
              </Typography>
              
              {selectedBooking.location?.address && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Location
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedBooking.location.address}
                  </Typography>
                </>
              )}
            </Grid>
            
            <Grid item xs={12} md={6}>
              {userType === 'supplier' && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Client
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {getBookingGuestDisplayName(selectedBooking)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {getBookingGuestEmailLine(selectedBooking)}
                  </Typography>
                </>
              )}
              
              {userType === 'client' && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Business
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedBooking.business?.name}
                  </Typography>
                </>
              )}
              
              {selectedBooking.clientNotes && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Notes
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedBooking.clientNotes}
                  </Typography>
                </>
              )}
            </Grid>
            
            {/* Team Members Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Assigned Team Members
                </Typography>
                {userType === 'supplier' && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleEditTeamMembers}
                  >
                    Edit booking
                  </Button>
                )}
              </Box>
              {selectedBooking.assignedTeamMembers?.length === 0 ? (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  No team members assigned. This booking needs to be assigned.
                </Alert>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {selectedBooking.assignedTeamMembers?.map((assignment, index) => {
                    const member = assignment.member;
                    const memberName = getMemberDisplayName(member);
                    return (
                      <Chip
                        key={index}
                        label={memberName}
                        size="small"
                        avatar={
                          <Avatar src={getAvatarSrc(getStaffMemberDisplayPhoto(member))}>
                            {memberName.charAt(0)}
                          </Avatar>
                        }
                      />
                    );
                  })}
                </Box>
              )}
            </Grid>
          </Grid>
          
          {selectedBooking.changeRequests && selectedBooking.changeRequests.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" gutterBottom>
                Change Requests
              </Typography>
              {selectedBooking.changeRequests.map((request, index) => (
                <Card key={index} sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="body2">
                      <strong>Type:</strong> {request.type}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Status:</strong> {request.status}
                    </Typography>
                    {request.reason && (
                      <Typography variant="body2">
                        <strong>Reason:</strong> {request.reason}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </>
          )}
        </DialogContent>
        
        <DialogActions>
          {userType === 'supplier' && selectedBooking.status === 'pending' && (
            <>
              <Button
                color="success"
                variant="contained"
                onClick={() => {
                  const id = selectedBooking._id;
                  setShowBookingDialog(false);
                  navigate('/supplier/bookings', { state: { openBookingApprove: id } });
                }}
              >
                Accept booking
              </Button>
              <Button
                color="error"
                variant="outlined"
                onClick={() => handleBookingAction(selectedBooking._id, 'updateStatus', {
                  status: 'rejected'
                })}
              >
                Decline
              </Button>
            </>
          )}
          
          {selectedBooking.status === 'confirmed' && (
            <Button
              color="error"
              onClick={() => handleBookingAction(selectedBooking._id, 'cancel')}
            >
              Cancel Booking
            </Button>
          )}
          
          <Button onClick={() => setShowBookingDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Render custom day view with staff on x-axis
  const renderDayViewWithStaff = () => {
    const dayStart = startOfDay(new Date(currentDate));
    const dayEnd = endOfDay(new Date(currentDate));
    const getNormalisedRange = (item) => {
      const start = new Date(item.startTime);
      const fallbackEnd = new Date(start.getTime() + (30 * 60 * 1000));
      const parsedEnd = item.endTime ? new Date(item.endTime) : fallbackEnd;
      const end = isNaN(parsedEnd.getTime()) || parsedEnd <= start ? fallbackEnd : parsedEnd;
      return { start, end };
    };

    const getDayClippedRange = (item) => {
      const { start, end } = getNormalisedRange(item);
      const clippedStart = start < dayStart ? dayStart : start;
      const clippedEnd = end > dayEnd ? dayEnd : end;
      if (clippedEnd <= clippedStart) return null;
      return { start, end, clippedStart, clippedEnd };
    };

    // Filter bookings and events for this day (overlap logic - matches backend and week/month views)
    // Include rescheduled bookings (they use startTime/endTime for the new date)
    const dayBookings = bookings.filter(booking => {
      if (typeFilters.length > 0 && !typeFilters.includes(getBookingTypeFilterKey(booking))) {
        return false;
      }
      const { start: bookingStart, end: bookingEnd } = getNormalisedRange(booking);
      return bookingStart < dayEnd && bookingEnd > dayStart;
    });

    const dayEvents = events.filter(event => {
      if (typeFilters.length > 0 && !typeFilters.includes(getEventTypeFilterKey(event))) {
        return false;
      }
      const { start: eventStart, end: eventEnd } = getNormalisedRange(event);
      return eventStart < dayEnd && eventEnd > dayStart;
    });

    const dayItems = [...dayBookings, ...dayEvents];
    const dayHours = getOpeningHoursForDate(currentDate);
    const isDayClosed = isClosedDate(currentDate) || !dayHours?.isOpen;
    const parseTimeToMinutes = (time) => {
      if (!time || typeof time !== 'string') return null;
      const [h, m] = time.split(':').map(Number);
      if (Number.isNaN(h) || Number.isNaN(m)) return null;
      return (h * 60) + m;
    };

    // Generate time slots based on day opening hours first, then extend for any out-of-hours appointments.
    const clippedDayRanges = dayItems
      .map((item) => getDayClippedRange(item))
      .filter(Boolean);
    const minStartMinutes = clippedDayRanges.length > 0
      ? Math.min(...clippedDayRanges.map((range) => (
        range.clippedStart.getHours() * 60 + range.clippedStart.getMinutes()
      )))
      : null;
    const maxEndMinutes = clippedDayRanges.length > 0
      ? Math.max(...clippedDayRanges.map((range) => (
        range.clippedEnd.getHours() * 60 + range.clippedEnd.getMinutes()
      )))
      : null;

    let roundedStartMinutes;
    let roundedEndMinutes;
    if (onlyOpeningHours) {
      // Trimmed view: opening hours, extended for any out-of-hours appointments, with padding.
      const openingStartMinutes = isDayClosed ? null : parseTimeToMinutes(dayHours?.openTime);
      const openingEndMinutes = isDayClosed ? null : parseTimeToMinutes(dayHours?.closeTime);
      const baseStartMinutes = openingStartMinutes ?? (minStartMinutes ?? (6 * 60));
      const baseEndMinutes = openingEndMinutes ?? (maxEndMinutes ?? (22 * 60));
      const combinedStartMinutes = minStartMinutes == null ? baseStartMinutes : Math.min(baseStartMinutes, minStartMinutes);
      const combinedEndMinutes = maxEndMinutes == null ? baseEndMinutes : Math.max(baseEndMinutes, maxEndMinutes);
      const paddedStartMinutes = Math.max(0, combinedStartMinutes - 60);
      const paddedEndMinutes = Math.min(24 * 60, combinedEndMinutes + 60);
      roundedStartMinutes = Math.floor(paddedStartMinutes / 30) * 30;
      roundedEndMinutes = Math.max(
        roundedStartMinutes + 30,
        Math.ceil(paddedEndMinutes / 30) * 30
      );
    } else {
      // Default: show the full 24 hours; closed times stay greyed out as a visual hint.
      roundedStartMinutes = 0;
      roundedEndMinutes = 24 * 60;
    }

    const timeSlots = [];
    for (let minutes = roundedStartMinutes; minutes < roundedEndMinutes; minutes += 30) {
      const hour = Math.floor(minutes / 60);
      const minute = minutes % 60;
      timeSlots.push({
        hour,
        minute,
        display: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
        time: minutes
      });
    }

    // Get appointments for each staff member
    const getAppointmentsForStaff = (member) => {
      const memberIds = new Set(
        [
          normaliseId(member?._id),
          normaliseId(member?.businessMember?._id || member?.businessMember),
          normaliseId(member?.staffMemberId)
        ].filter(Boolean)
      );

      if (memberIds.size === 0) {
        return [];
      }

      return [
        ...dayBookings.filter(booking => 
          booking.assignedTeamMembers?.some(tm => {
            const assignmentIds = getAssignmentIds(tm);
            return assignmentIds.some(id => memberIds.has(id));
          })
        ),
        ...dayEvents.filter(event => {
          // In day view, only show events in a staff column when explicitly assigned.
          // Otherwise they belong in the unassigned/assigned elsewhere column.
          return event.assignedTeamMembers?.some(tm => {
            const assignmentIds = getAssignmentIds(tm);
            return assignmentIds.some(id => memberIds.has(id));
          });
        })
      ];
    };

    const getMemberNameFromAssignment = (assignment) => {
      const memberName = getMemberDisplayName(assignment?.member);
      if (memberName !== 'Unnamed') return memberName;
      const staffMemberName = String(assignment?.staffMember?.name || '').trim();
      return staffMemberName || 'Unnamed';
    };

    // Build candidate day columns from configured team members and assignment metadata.
    const membersById = new Map();
    teamMembers.forEach((member) => {
      const primaryId = normaliseId(member?._id || member?.businessMember?._id || member?.businessMember || member?.staffMemberId);
      const altId = normaliseId(member?.staffMemberId);
      if (primaryId) membersById.set(primaryId, member);
      if (altId) membersById.set(altId, member);
    });

    const dayItemsForColumns = [...dayBookings, ...dayEvents];
    dayItemsForColumns.forEach((item) => {
      (item.assignedTeamMembers || []).forEach((assignment) => {
        const assignmentIds = getAssignmentIds(assignment);
        if (assignmentIds.length === 0) return;
        const resolvedName = getMemberNameFromAssignment(assignment);
        if (resolvedName === 'Unnamed') return;
        const existingMember = assignmentIds.map((id) => membersById.get(id)).find(Boolean);

        if (existingMember) {
          const existingDisplayName = getMemberDisplayName(existingMember);
          if (existingDisplayName === 'Unnamed') {
            existingMember.staffMemberName = existingMember.staffMemberName || assignment?.member?.staffMemberName || assignment?.staffMember?.name || null;
            existingMember.name = existingMember.name && existingMember.name !== 'Unnamed'
              ? existingMember.name
              : getMemberNameFromAssignment(assignment);
            existingMember.user = existingMember.user || assignment?.member?.user || assignment?.staffMember?.user || null;
            existingMember.profileImage = existingMember.profileImage || assignment?.member?.profileImage || assignment?.staffMember?.profileImage || null;
          }
          assignmentIds.forEach((id) => {
            if (id && !membersById.has(id)) {
              membersById.set(id, existingMember);
            }
          });
          return;
        }

        const preferredId = assignmentIds[0];
        const resolvedMember = {
          _id: preferredId,
          staffMemberId: assignmentIds.find((id) => id !== preferredId) || preferredId,
          user: assignment?.member?.user || assignment?.staffMember?.user || null,
          name: resolvedName,
          staffMemberName: assignment?.member?.staffMemberName || assignment?.staffMember?.name || null,
          businessMember: assignment?.member || null,
          profileImage: assignment?.member?.profileImage || assignment?.staffMember?.profileImage || null
        };
        assignmentIds.forEach((id) => {
          if (id) membersById.set(id, resolvedMember);
        });
      });
    });

    let membersWithShifts = Array.from(membersById.values());
    const dedupedMembersByCanonicalId = new Map();
    membersWithShifts.forEach((member) => {
      const canonicalId = getMemberCanonicalId(member);
      if (!canonicalId) return;
      const existingMember = dedupedMembersByCanonicalId.get(canonicalId);
      if (!existingMember) {
        dedupedMembersByCanonicalId.set(canonicalId, member);
        return;
      }

      dedupedMembersByCanonicalId.set(canonicalId, {
        ...existingMember,
        ...member,
        _id: existingMember._id || member._id,
        staffMemberId: existingMember.staffMemberId || member.staffMemberId,
        businessMember: existingMember.businessMember || member.businessMember,
        user: existingMember.user || member.user,
        profileImage: existingMember.profileImage || member.profileImage,
        name: getMemberDisplayName(existingMember) !== 'Unnamed'
          ? existingMember.name
          : member.name,
      });
    });
    membersWithShifts = Array.from(dedupedMembersByCanonicalId.values());

    // Day view columns should show:
    // 1) staff with a shift on the selected day, or
    // 2) anyone explicitly assigned to a booking/event on that day.
    const scheduledStaffIdsForDay = new Set(
      (teamMemberShifts || [])
        .map((shift) => normaliseId(shift.teamMember?._id || shift.teamMember))
        .filter(Boolean)
    );
    const assignedStaffIdsForDay = new Set();
    dayItemsForColumns.forEach((item) => {
      (item.assignedTeamMembers || []).forEach((assignment) => {
        getAssignmentIds(assignment).forEach((id) => {
          if (id) assignedStaffIdsForDay.add(id);
        });
      });
    });

    membersWithShifts = membersWithShifts.filter((member) => {
      const candidateIds = [
        normaliseId(member.staffMemberId),
        normaliseId(member._id),
        normaliseId(member.businessMember?._id || member.businessMember),
      ].filter(Boolean);
      return candidateIds.some((id) => (
        scheduledStaffIdsForDay.has(id) || assignedStaffIdsForDay.has(id)
      ));
    });

    // Filter by visibleUsers if specified
    const visibleUsers = calendarSettings?.visibleUsers || [];
    if (visibleUsers.length > 0 && teamMembers.length > 0 && visibleUsers.length < teamMembers.length) {
      membersWithShifts = membersWithShifts.filter(member => {
        // member._id is set to BusinessMember ID in fetchTeamMembers
        const memberId = member._id || member.businessMember?._id || member.businessMember;
        return memberId && visibleUsers.some(visibleId => 
          memberId.toString() === visibleId.toString()
        );
      });
    }

    // Get unassigned bookings and any assigned bookings that are hidden by the visible-user filter.
    const memberIdsWithShifts = new Set(
      membersWithShifts.flatMap((m) => [
        normaliseId(m._id || m.businessMember?._id || m.businessMember),
        normaliseId(m.staffMemberId)
      ]).filter(Boolean)
    );
    const unassignedBookings = dayBookings.filter(booking => {
      const assignedMembers = booking.assignedTeamMembers || [];
      if (assignedMembers.length === 0) return true;
      // Orphaned: assigned to staff who don't have a visible column.
      const hasVisibleColumn = assignedMembers.some(tm => {
        const ids = getAssignmentIds(tm);
        return ids.some(id => memberIdsWithShifts.has(id));
      });
      return !hasVisibleColumn;
    });

    const unassignedEvents = dayEvents.filter((event) => {
      const assignedMembers = event.assignedTeamMembers || [];
      if (assignedMembers.length === 0) return true;
      const hasVisibleColumn = assignedMembers.some((tm) => {
        const ids = getAssignmentIds(tm);
        return ids.some((id) => memberIdsWithShifts.has(id));
      });
      return !hasVisibleColumn;
    });

    const unassignedItems = [
      ...unassignedBookings.map((booking) => ({ type: 'booking', item: booking })),
      ...unassignedEvents.map((event) => ({ type: 'event', item: event }))
    ];
    const hasTrulyUnassigned = unassignedItems.some(({ item }) => (item.assignedTeamMembers?.length || 0) === 0);
    const hasAssignedButHidden = unassignedItems.some(({ item }) => (item.assignedTeamMembers?.length || 0) > 0);

    return (
      <Box sx={{ width: '100%', pb: 0, mb: 0, backgroundColor: 'background.default' }} className="day-view-container">
        <Box sx={{ display: 'flex', border: '1px solid', borderColor: 'divider', minHeight: 600, flexDirection: 'column', pb: 0, mb: 0, backgroundColor: 'background.default' }}>
          {openingHoursLoading && (
            <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                Loading opening hours for this day...
              </Typography>
            </Box>
          )}
          {/* Message at top if no assigned team columns for this day - scrolls with table */}
          {membersWithShifts.length === 0 && dayBookings.length === 0 && dayEvents.length === 0 && (
            <Box sx={{
              width: '100%',
              backgroundColor: 'background.paper',
              borderBottom: '2px solid',
              borderColor: 'divider',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
              position: 'sticky',
              top: 0,
              zIndex: 4,
            }}>
              <Typography 
                variant="body2" 
                sx={{
                  color: 'text.secondary',
                  fontWeight: 'bold'
                }}
              >
                No appointments for this day yet. Use the unassigned column to add one.
              </Typography>
            </Box>
          )}
          {/* Time column and staff columns - scrollable container */}
          <Box sx={{ 
            display: 'flex', 
            flex: 1, 
            pb: 0, 
            mb: 0, 
            overflow: 'visible',
            backgroundColor: 'background.default',
            position: 'relative',
          }}>
            {/* Time column */}
          <Box 
            className="day-view-time-column"
            style={{
              borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
              borderBottomColor: 'rgba(0, 0, 0, 0.1)',
            }}
            sx={{ 
            minWidth: 80, 
            borderRight: '1px solid', 
            borderBottom: '1px solid',
            borderBottomColor: 'rgba(0, 0, 0, 0.1) !important',
            borderColor: 'divider',
            backgroundColor: 'background.default',
            position: 'sticky',
            left: 0,
            zIndex: 2,
            borderRadius: 0,
            pb: 0,
            mb: 0,
            '& .MuiBox-root': {
              borderBottomColor: `${theme.palette.divider} !important`,
            },
          }}>
            <Box sx={{ 
              height: 60, 
              borderBottom: '1px solid', 
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              backgroundColor: 'background.paper',
              position: 'sticky',
              top: 0,
              zIndex: 3,
            }}>
              Time
            </Box>
            {timeSlots.map((slot, idx) => {
              return (
                <Box
                  key={idx}
                  className="day-view-time-slot"
                  style={{
                    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                    borderBottomColor: 'rgba(0, 0, 0, 0.1)',
                  }}
                  sx={{
                    height: 48,
                    borderBottom: '1px solid',
                    borderBottomColor: 'rgba(0, 0, 0, 0.1) !important',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    backgroundColor: 'background.paper',
                  }}
                >
                  {slot.display}
                </Box>
              );
            })}
          </Box>

          {/* Staff columns */}
          {membersWithShifts.length > 0 ? membersWithShifts.map((member) => {
            // Use BusinessMember ID for matching bookings (bookings reference BusinessMember)
            const memberAppointments = getAppointmentsForStaff(member);
            const memberName = getMemberDisplayName(member);
            const memberAppointmentLayout = (() => {
              const layoutByAppointment = new Map();
              const sortedAppointments = memberAppointments
                .map((apt) => {
                  const aptRange = getDayClippedRange(apt);
                  if (!aptRange) return null;
                  return {
                    apt,
                    start: aptRange.clippedStart,
                    end: aptRange.clippedEnd
                  };
                })
                .filter(Boolean)
                .sort((a, b) => {
                  const startDiff = a.start.getTime() - b.start.getTime();
                  if (startDiff !== 0) return startDiff;
                  return b.end.getTime() - a.end.getTime();
                });

              const assignClusterLayout = (cluster) => {
                if (!cluster || cluster.length === 0) return;
                const columnEndTimes = [];
                const clusterLayout = [];

                cluster.forEach((item) => {
                  let columnIndex = columnEndTimes.findIndex((endTime) => endTime <= item.start);
                  if (columnIndex === -1) {
                    columnIndex = columnEndTimes.length;
                    columnEndTimes.push(item.end);
                  } else {
                    columnEndTimes[columnIndex] = item.end;
                  }
                  clusterLayout.push({ apt: item.apt, columnIndex });
                });

                const totalColumns = Math.max(columnEndTimes.length, 1);
                clusterLayout.forEach(({ apt, columnIndex }) => {
                  layoutByAppointment.set(apt, { columnIndex, totalColumns });
                });
              };

              let cluster = [];
              let clusterMaxEnd = null;
              sortedAppointments.forEach((item) => {
                if (cluster.length === 0) {
                  cluster = [item];
                  clusterMaxEnd = item.end;
                  return;
                }

                // Same overlap behaviour as FullCalendar: touching endpoints are non-overlapping.
                if (item.start < clusterMaxEnd) {
                  cluster.push(item);
                  if (item.end > clusterMaxEnd) {
                    clusterMaxEnd = item.end;
                  }
                } else {
                  assignClusterLayout(cluster);
                  cluster = [item];
                  clusterMaxEnd = item.end;
                }
              });
              assignClusterLayout(cluster);

              return layoutByAppointment;
            })();
            
            return (
              <Box
                key={member._id}
                sx={{
                  flex: 1,
                  minWidth: 200,
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  position: 'relative',
                  backgroundColor: 'background.default',
                }}
              >
                {/* Staff header */}
                <Box sx={{
                  height: 60,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  backgroundColor: 'background.default',
                  position: 'sticky',
                  top: membersWithShifts.length === 0 ? '73px' : 0,
                  zIndex: 3,
                  width: '100%',
                }}>
                  <Avatar 
                    src={getAvatarSrc(getStaffMemberDisplayPhoto(member))}
                    sx={{ 
                      width: 40, 
                      height: 40, 
                      mr: 1,
                      border: '2px solid',
                      borderColor: 'primary.main',
                    }}
                    alt={memberName}
                  >
                    {memberName.charAt(0)}
                  </Avatar>
                  <Typography variant="body2" noWrap>
                    {memberName}
                  </Typography>
                </Box>

                {/* Time slots with appointments */}
                {timeSlots.map((slot, slotIdx) => {
                  const slotStart = new Date(currentDate);
                  slotStart.setHours(slot.hour, slot.minute, 0, 0);
                  const slotEnd = new Date(slotStart);
                  slotEnd.setMinutes(slotEnd.getMinutes() + 30);

                  // Find appointments that overlap with this slot
                  const overlappingAppointments = memberAppointments.filter(apt => {
                    const aptRange = getDayClippedRange(apt);
                    if (!aptRange) return false;
                    return aptRange.clippedStart < slotEnd && aptRange.clippedEnd > slotStart;
                  });

                  // Render each appointment card only once at its start slot.
                  const appointmentsStartingInSlot = overlappingAppointments.filter((apt) => {
                    const aptRange = getDayClippedRange(apt);
                    if (!aptRange) return false;
                    return aptRange.clippedStart >= slotStart && aptRange.clippedStart < slotEnd;
                  });

                  return (
                    <Box
                      key={slotIdx}
                      style={{
                        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                        borderBottomColor: 'rgba(0, 0, 0, 0.1)',
                      }}
                      sx={{
                        height: 48,
                        borderBottom: '1px solid',
                        borderBottomColor: 'rgba(0, 0, 0, 0.1) !important',
                        position: 'relative',
                        backgroundColor: isWithinOpeningHours(slotStart) ? 'background.paper' : 'action.hover',
                        opacity: isWithinOpeningHours(slotStart) ? 1 : 0.65,
                        cursor: isPastDate(slotStart) ? 'not-allowed' : 'pointer',
                        pointerEvents: 'auto',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          cursor: isPastDate(slotStart) ? 'not-allowed' : 'pointer',
                        },
                      }}
                      onClick={() => {
                        if (overlappingAppointments.length > 0) {
                          const apt = overlappingAppointments[0];
                          if (apt.serviceType) {
                            // If onBookingClick is provided, use it to fetch fresh data
                            // Otherwise, use the local dialog
                            if (onBookingClick) {
                              onBookingClick(apt);
                            } else {
                              setSelectedBooking(apt);
                              setShowBookingDialog(true);
                            }
                          } else if (onEventClick) {
                            onEventClick(apt);
                          }
                        } else {
                          if (isPastDate(slotStart)) {
                            return;
                          }
                          // Clicking empty time slot - create new booking at this time
                          // Day view: we know date, time, and staff member
                          const clickedDateTime = new Date(slotStart);
                          const dateStr = clickedDateTime.toISOString().split('T')[0]; // Format: YYYY-MM-DD
                          const timeStr = clickedDateTime.toTimeString().split(' ')[0].substring(0, 5); // Format: HH:MM
                          const staffMemberId = member.staffMemberId || member._id; // Prefer schedules staff ID for manual booking preselection
                          navigate(`/supplier/bookings/new?date=${dateStr}&time=${timeStr}&staffMember=${staffMemberId}`);
                        }
                      }}
                    >
                      {appointmentsStartingInSlot.map((apt, aptIdx) => {
                        const aptRange = getDayClippedRange(apt);
                        if (!aptRange) return null;
                        const { clippedStart: aptStart, clippedEnd: aptEnd } = aptRange;
                        const startMinutes = aptStart.getHours() * 60 + aptStart.getMinutes();
                        const endMinutes = aptEnd.getHours() * 60 + aptEnd.getMinutes();
                        const duration = endMinutes - startMinutes;
                        const topOffset = ((startMinutes % 30) / 30) * 48;
                        const height = (duration / 30) * 48;

                        const isEvent = !apt.serviceType;
                        const isCancelled = isEvent ? (apt.status === 'cancelled' || apt.isCancelled) : false;
                        const person = apt.serviceType ? getBookingDisplayPerson(apt) : null;
                        const personName = person?.name || 'Unnamed';
                        const personAvatar = person?.avatar || getAvatarSrc(null);
                        // In staff columns the person row shows the staff member; surface the couple too
                        const aptGuestName = !isEvent && (apt.assignedTeamMembers || []).length > 0
                          ? getBookingGuestDisplayName(apt)
                          : '';
                        const backgroundColor = isEvent 
                          ? (isCancelled
                              ? '#9e9e9e'
                              : apt.color ||
                                (apt.isBlocker ? '#f44336' : '#ff9800'))
                          : (apt.status === 'cancelled'
                              ? getStatusColor(apt.status)
                              : (apt.serviceType?.color || getStatusColor(apt.status)));
                        const overlapLayout = memberAppointmentLayout.get(apt) || { columnIndex: 0, totalColumns: 1 };
                        const widthPercent = 100 / overlapLayout.totalColumns;
                        const leftPercent = overlapLayout.columnIndex * widthPercent;

                        return (
                          <Box
                            key={aptIdx}
                            title={getInlineCalendarItemTooltipText(apt)}
                            aria-label={getInlineCalendarItemTooltipText(apt)}
                            sx={{
                              position: 'absolute',
                              top: `${topOffset}px`,
                              left: `calc(${leftPercent}% + 1px)`,
                              width: `calc(${widthPercent}% - 2px)`,
                              height: `${Math.max(height, 24)}px`,
                              backgroundColor,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              p: 0.5,
                              fontSize: '0.75rem',
                              overflow: 'hidden',
                              cursor: 'pointer',
                              zIndex: 10,
                              '&:hover': {
                                zIndex: 11,
                                boxShadow: theme.shadows[4],
                              },
                            }}
                            style={{ zIndex: 10 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (apt.serviceType) {
                                // If onBookingClick is provided, use it to fetch fresh data
                                // Otherwise, use the local dialog
                                if (onBookingClick) {
                                  onBookingClick(apt);
                                } else {
                                  setSelectedBooking(apt);
                                  setShowBookingDialog(true);
                                }
                              } else if (onEventClick) {
                                onEventClick(apt);
                              }
                            }}
                          >
                            {!isEvent && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                                <Avatar src={personAvatar} sx={{ width: 14, height: 14, fontSize: '0.55rem' }}>
                                  {personName.charAt(0)}
                                </Avatar>
                                <Typography variant="caption" sx={{ fontSize: '0.62rem' }} noWrap>
                                  {personName}
                                </Typography>
                              </Box>
                            )}
                            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }} noWrap>
                              {isEvent ? apt.title : apt.serviceType?.name}
                            </Typography>
                            {aptGuestName && (
                              <Typography variant="caption" sx={{ fontSize: '0.62rem', display: 'block' }} noWrap>
                                {aptGuestName}
                              </Typography>
                            )}
                            <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                              {new Date(apt.startTime || apt.startTime).toLocaleTimeString([], { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })}
              </Box>
            );
          }) : null}

          {/* Unassigned column - also acts as the supplier fallback when no staff are set up. */}
          {(unassignedItems.length > 0 || membersWithShifts.length === 0) && (
            <Box
              sx={{
                flex: 1,
                minWidth: 200,
                borderRight: '1px solid',
                borderColor: 'divider',
                position: 'relative',
                backgroundColor: 'background.default',
              }}
            >
              {/* Unassigned header */}
              <Box 
                sx={{
                  height: 60,
                  borderBottom: '1px solid',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  backgroundColor: '#fff3cd', // Light yellow/amber background for unassigned
                  position: 'sticky',
                  top: membersWithShifts.length === 0 ? '73px' : 0,
                  zIndex: 3,
                  width: '100%',
                  border: '2px dashed #ffc107 !important', // Amber border with important
                }}
              >
                <Typography variant="body2" noWrap sx={{ color: '#856404', fontWeight: 'bold' }}>
                  {hasTrulyUnassigned && hasAssignedButHidden
                    ? 'Unassigned / Assigned elsewhere'
                    : hasAssignedButHidden
                      ? 'Assigned elsewhere'
                      : 'Unassigned'}
                </Typography>
              </Box>

              {/* Time slots with unassigned appointments */}
              {timeSlots.map((slot, slotIdx) => {
                const slotStart = new Date(currentDate);
                slotStart.setHours(slot.hour, slot.minute, 0, 0);
                const slotEnd = new Date(slotStart);
                slotEnd.setMinutes(slotEnd.getMinutes() + 30);

                // Find unassigned bookings/events that overlap with this slot
                const overlappingUnassigned = unassignedItems.filter(({ item }) => {
                  const itemRange = getDayClippedRange(item);
                  if (!itemRange) return false;
                  return itemRange.clippedStart < slotEnd && itemRange.clippedEnd > slotStart;
                });

                // Render each unassigned item card only once at its start slot.
                const unassignedStartingInSlot = overlappingUnassigned.filter(({ item }) => {
                  const itemRange = getDayClippedRange(item);
                  if (!itemRange) return false;
                  return itemRange.clippedStart >= slotStart && itemRange.clippedStart < slotEnd;
                });

                return (
                  <Box
                    key={slotIdx}
                    style={{
                      borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                      borderBottomColor: 'rgba(0, 0, 0, 0.1)',
                    }}
                    sx={{
                      height: 48,
                      borderBottom: '1px solid',
                      borderBottomColor: 'rgba(0, 0, 0, 0.1) !important',
                      position: 'relative',
                      backgroundColor: isWithinOpeningHours(slotStart) ? 'background.paper' : 'action.hover',
                      opacity: isWithinOpeningHours(slotStart) ? 1 : 0.65,
                      cursor: isPastDate(slotStart) ? 'not-allowed' : 'pointer',
                      pointerEvents: 'auto',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        cursor: isPastDate(slotStart) ? 'not-allowed' : 'pointer',
                      },
                    }}
                    onClick={() => {
                      if (overlappingUnassigned.length > 0) {
                        const { type: firstType, item: firstItem } = overlappingUnassigned[0];
                        // If onBookingClick is provided, use it to fetch fresh data
                        // Otherwise, use the local dialog
                        if (firstType === 'booking' && onBookingClick) {
                          onBookingClick(firstItem);
                        } else if (firstType === 'booking') {
                          setSelectedBooking(firstItem);
                          setShowBookingDialog(true);
                        } else if (onEventClick) {
                          onEventClick(firstItem);
                        } else {
                          return;
                        }
                      } else {
                        if (isPastDate(slotStart)) {
                          return;
                        }
                        const clickedDateTime = new Date(slotStart);
                        const dateStr = clickedDateTime.toISOString().split('T')[0];
                        const timeStr = clickedDateTime.toTimeString().split(' ')[0].substring(0, 5);
                        navigate(`/supplier/bookings/new?date=${dateStr}&time=${timeStr}`);
                      }
                    }}
                  >
                    {unassignedStartingInSlot.map(({ type, item }, bookingIdx) => {
                      const itemRange = getDayClippedRange(item);
                      if (!itemRange) return null;
                      const { clippedStart: bookingStart, clippedEnd: bookingEnd } = itemRange;
                      const startMinutes = bookingStart.getHours() * 60 + bookingStart.getMinutes();
                      const endMinutes = bookingEnd.getHours() * 60 + bookingEnd.getMinutes();
                      const duration = endMinutes - startMinutes;
                      const topOffset = ((startMinutes % 30) / 30) * 48;
                      const height = (duration / 30) * 48;
                      const isBookingItem = type === 'booking';
                      const person = isBookingItem
                        ? getBookingDisplayPerson(item)
                        : null;
                      const personName = person?.name || (isBookingItem ? 'Unnamed' : 'Event');
                      const personAvatar = person?.avatar || getAvatarSrc(null);

                      return (
                        <Box
                          key={bookingIdx}
                          title={getInlineCalendarItemTooltipText(item)}
                          aria-label={getInlineCalendarItemTooltipText(item)}
                          sx={{
                            position: 'absolute',
                            top: `${topOffset}px`,
                            left: 0,
                            right: 0,
                            height: `${Math.max(height, 24)}px`,
                            backgroundColor: '#fff3cd', // Light yellow/amber for unassigned
                            border: '2px dashed',
                            borderColor: '#ffc107', // Amber border
                            borderRadius: 1,
                            p: 0.5,
                            fontSize: '0.75rem',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            zIndex: 10,
                            '&:hover': {
                              zIndex: 11,
                              boxShadow: theme.shadows[4],
                              backgroundColor: '#ffe082', // Slightly darker on hover
                            },
                          }}
                          style={{ zIndex: 10 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            // If onBookingClick is provided, use it to fetch fresh data
                            // Otherwise, use the local dialog
                            if (isBookingItem && onBookingClick) {
                              onBookingClick(item);
                            } else if (isBookingItem) {
                              setSelectedBooking(item);
                              setShowBookingDialog(true);
                            } else if (onEventClick) {
                              onEventClick(item);
                            } else {
                              return;
                            }
                          }}
                        >
                          {isBookingItem && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                              <Avatar src={personAvatar} sx={{ width: 14, height: 14, fontSize: '0.55rem' }}>
                                {personName.charAt(0)}
                              </Avatar>
                              <Typography variant="caption" sx={{ fontSize: '0.62rem', color: '#856404' }} noWrap>
                                {personName}
                              </Typography>
                            </Box>
                          )}
                          <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', color: '#856404' }}>
                            {isBookingItem ? (item.serviceType?.name || 'Appointment') : (item.title || 'Event')}
                            {isBookingItem && (item.assignedTeamMembers?.length || 0) === 0 ? ' (Unassigned)' : ''}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#856404' }}>
                            {new Date(item.startTime).toLocaleTimeString([], { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          )}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Container
      maxWidth="xl"
      disableGutters={isMobile}
      sx={{ py: { xs: 0, lg: 4 }, px: { xs: 0, lg: 3 }, width: '100%', maxWidth: '100%', minWidth: 0 }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3, mx: { xs: 1.5, lg: 0 } }}>
          {error}
        </Alert>
      )}

      <Card sx={{ 
        backgroundColor: 'transparent', 
        backdropFilter: 'none',
        ...(isMobile && { overflow: 'visible' }),
        // Override theme rule that makes nested Cards solid white
        '& .MuiCardContent-root .MuiCard-root': {
          backgroundColor: 'transparent',
          backdropFilter: 'none',
        },
      }}>
        {/* Toggle for showing cancelled appointments - top right above calendar controls */}
        <Box sx={{ 
          p: { xs: 1.5, sm: 2 }, 
          pb: { xs: 0, sm: 0 },
          mb: { xs: 0.5, sm: 2 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: 'wrap',
          gap: { xs: 0.5, sm: 1 },
          minWidth: 0,
        }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
              minWidth: 0,
            }}
          >
            <ToggleButtonGroup
              value={scheduleViewMode}
              exclusive
              size="small"
              onChange={(_, nextValue) => {
                if (!nextValue) return;
                setScheduleViewMode(nextValue);
              }}
              sx={{
                '& .MuiToggleButton-root': {
                  px: { xs: 1.25, sm: 1.5 },
                  textTransform: 'none',
                  fontWeight: 600
                }
              }}
            >
              <ToggleButton value="calendar">Calendar</ToggleButton>
              <ToggleButton value="list">List</ToggleButton>
            </ToggleButtonGroup>

            {isMobile && userType === 'supplier' && (
              <>
                <Button
                  variant="contained"
                  size="small"
                  endIcon={<CaretDown size={14} />}
                  onClick={handleCreateNewClick}
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    fontSize: '0.8125rem',
                    backgroundColor: '#000',
                    color: '#fff',
                    borderRadius: 1,
                    flexShrink: 0,
                    '&:hover': { backgroundColor: '#333' },
                  }}
                >
                  Create New
                </Button>
                <Menu
                  anchorEl={createNewAnchorEl}
                  open={createNewMenuOpen}
                  onClose={handleCreateNewClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  <MenuItem onClick={handleCreateNewAppointment}>
                    <Plus size={20} style={{ marginRight: 8 }} />
                    New Appointment
                  </MenuItem>
                  <MenuItem onClick={handleCreateNewEvent}>
                    <CalendarCheck size={20} style={{ marginRight: 8 }} />
                    New Event
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'center', sm: 'flex-start' },
              gap: { xs: 1, sm: 1 },
              flexWrap: 'wrap',
              minWidth: 0,
              rowGap: 0.5,
              mt: { xs: 1.5, sm: 0 },
              mb: { xs: 0, sm: 0 },
              pb: 0,
            }}
          >
            {userType === 'supplier' && scheduleViewMode === 'calendar' && (view === 'day' || view === 'week') && (
              <FormControlLabel
                control={
                  <Switch
                    checked={onlyOpeningHours}
                    onChange={(e) => handleOnlyOpeningHoursChange(e.target.checked)}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                  />
                }
                label={isMobile ? 'Opening hours' : 'Only Show Opening Hours'}
                labelPlacement="start"
                sx={{
                  m: 0,
                  mr: { xs: 1, sm: 0 },
                  '& .MuiFormControlLabel-label': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    fontWeight: 'bold',
                    paddingRight: '8px',
                    whiteSpace: 'nowrap',
                  }
                }}
              />
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={showCancelled}
                  onChange={(e) => setShowCancelled(e.target.checked)}
                  color="primary"
                  size={isMobile ? 'small' : 'medium'}
                />
              }
              label={isMobile ? 'Cancelled' : 'Show Cancelled'}
              labelPlacement="start"
              sx={{
                m: 0,
                '& .MuiFormControlLabel-label': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  fontWeight: 'bold',
                  paddingRight: '8px',
                  whiteSpace: 'nowrap',
                }
              }}
            />
          </Box>
        </Box>

        {scheduleViewMode === 'list' ? (
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, pt: 0 }}>
            {listViewLoading ? (
              <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {listViewGroups.filter((group) => group.items.length > 0).length === 0 ? (
                  <Alert severity="info">
                    No scheduled bookings found for these date ranges.
                  </Alert>
                ) : (
                  listViewGroups.map((group) => {
                    if (group.items.length === 0) return null;
                    return (
                      <Box key={group.key}>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 700 }}>
                          {group.label}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                          {group.items.map((item) => {
                            const isBooking = item.type === 'booking';
                            const booking = item.booking;
                            const event = item.event;
                            const isCancelled = isBooking
                              ? booking?.status === 'cancelled'
                              : event?.status === 'cancelled' || event?.isCancelled;
                            const rowDate = new Date(item.startTime);
                            const guestName = isBooking ? getBookingGuestDisplayName(booking) : '';
                            const startTimeLabel = format(rowDate, 'h:mm a');
                            const endTimeLabel = format(new Date(item.endTime || item.startTime), 'h:mm a');
                            const assignedPeople = ((isBooking ? booking?.assignedTeamMembers : event?.assignedTeamMembers) || [])
                              .map((assignment) => getAssignmentDisplayPerson(assignment)?.name)
                              .filter(Boolean);
                            return (
                              <React.Fragment key={`${item.type}-${item.id}`}>
                                <Card
                                onClick={() => {
                                  if (isBooking) {
                                    openBookingDetails(booking);
                                  } else if (onEventClick && event) {
                                    onEventClick(event);
                                  }
                                }}
                                sx={{
                                  cursor: 'pointer',
                                  borderRadius: 0,
                                  border: 'none',
                                  boxShadow: 'none',
                                  opacity: isCancelled ? 0.25 : 1,
                                  '&:hover': { backgroundColor: 'action.hover' }
                                }}
                              >
                                <Box
                                  sx={{
                                    py: 1,
                                    px: 1.25,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    flexWrap: { xs: 'wrap', sm: 'nowrap' }
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 60,
                                      minWidth: 60,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      borderRadius: 1,
                                      py: 1,
                                      textAlign: 'center',
                                      backgroundColor: '#fff'
                                    }}
                                  >
                                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', lineHeight: 1.1 }}>
                                      {format(rowDate, 'MMM').toUpperCase()}
                                    </Typography>
                                    <Typography variant="h5" sx={{ lineHeight: 1.05, fontWeight: 700 }}>
                                      {format(rowDate, 'd')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.1 }}>
                                      {format(rowDate, 'yyyy')}
                                    </Typography>
                                  </Box>

                                  <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                                      {isBooking
                                        ? `${booking?.serviceType?.name || 'Appointment'}${guestName ? ` with ${guestName}` : ' with unassigned couple'}`
                                        : (event?.title || 'Event')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.25 }}>
                                      {startTimeLabel} - {endTimeLabel}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                      {assignedPeople.length > 0 ? `Assigned: ${assignedPeople.join(', ')}` : 'Assigned: Unassigned'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Card>
                              </React.Fragment>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            )}
          </CardContent>
        ) : isMobile ? (
          <Box sx={{ px: 1.5, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5, position: 'relative', overflow: 'visible', minWidth: 0, width: '100%' }}>
            {loading && (
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'rgba(255,255,255,0.7)', zIndex: 1 }}>
                <CircularProgress size={28} />
              </Box>
            )}
            <ToggleButtonGroup
              value={mobileViewMode}
              exclusive
              onChange={(_, v) => v && setMobileViewMode(v)}
              size="small"
              fullWidth
              sx={{
                '& .MuiToggleButton-root': {
                  flex: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 1,
                },
              }}
            >
              <ToggleButton value="week"><Columns size={18} style={{ marginRight: 6 }} />Week</ToggleButton>
              <ToggleButton value="calendar"><CalendarBlank size={18} style={{ marginRight: 6 }} />Calendar</ToggleButton>
            </ToggleButtonGroup>

            {mobileViewMode === 'week' ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  <IconButton size="small" onClick={() => setMobileWeekStart(d => addDays(d, -7))} sx={{ minWidth: 44, minHeight: 44, flexShrink: 0 }}>
                    <NavigateBefore fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setMobileWeekStart(d => addDays(d, 7))} sx={{ minWidth: 44, minHeight: 44, flexShrink: 0 }}>
                    <NavigateNext fontSize="small" />
                  </IconButton>
                  <Typography variant="body2" sx={{ ml: 0.5, minWidth: 0, fontSize: '0.8125rem' }} noWrap>
                    {format(mobileWeekStart, 'd MMM')} – {format(endOfWeek(mobileWeekStart, { weekStartsOn: calendarSettings?.startOfWeek ?? 1 }), 'd MMM yyyy')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', overflowX: 'auto', gap: 1, pb: 1, minHeight: 0, minWidth: 0, width: '100%', WebkitOverflowScrolling: 'touch', mx: -1.5, px: 1.5 }}>
                  {eachDayOfInterval({ start: mobileWeekStart, end: endOfWeek(mobileWeekStart, { weekStartsOn: calendarSettings?.startOfWeek ?? 1 }) }).map((day) => {
                    const dayKey = format(day, 'yyyy-MM-dd');
                    const dayEvents = calendarEvents.filter((e) => {
                      const es = new Date(e.start);
                      const ee = new Date(e.end);
                      const ds = new Date(day);
                      ds.setHours(0, 0, 0, 0);
                      const de = new Date(day);
                      de.setHours(23, 59, 59, 999);
                      return es < de && ee > ds;
                    });
                    return (
                      <Box key={dayKey} sx={{ flex: '0 0 112px', minWidth: 112, maxWidth: 112, border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ p: 1, bgcolor: 'action.hover', cursor: 'pointer', minHeight: 44, touchAction: 'manipulation', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', '&:active': { bgcolor: 'action.selected' } }} onClick={() => setDayModal({ open: true, date: day })}>
                          <Typography variant="caption" color="text.secondary">{format(day, 'EEE')}</Typography>
                          <Typography variant="subtitle2" fontWeight={600}>{format(day, 'd')}</Typography>
                          {dayEvents.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.25, mt: 0.25 }}>
                              {[...Array(Math.min(3, dayEvents.length))].map((_, i) => (
                                <Box key={i} sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'primary.main' }} />
                              ))}
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ p: 1, flex: 1, overflow: 'auto', maxHeight: 200 }}>
                          {dayEvents.slice(0, 5).map((event) => (
                            <Box key={event.id} onClick={(e) => { e.stopPropagation(); handleEventClick({ event }); }} sx={{ p: 0.75, mb: 0.5, borderRadius: 1, backgroundColor: event.backgroundColor || 'action.hover', border: '1px solid', borderColor: event.borderColor || 'divider', cursor: 'pointer', touchAction: 'manipulation', minHeight: 36 }}>
                              <Typography variant="caption" fontWeight={600} display="block" noWrap>{event.title}</Typography>
                              <Typography variant="caption" color="text.secondary">{event.start ? format(new Date(event.start), 'h:mm a') : ''} – {event.end ? format(new Date(event.end), 'h:mm a') : ''}</Typography>
                            </Box>
                          ))}
                          {dayEvents.length > 5 && <Typography variant="caption" color="text.secondary">+{dayEvents.length - 5} more</Typography>}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minWidth: 0 }}>
                  <IconButton size="small" onClick={() => setMobileMonthStart(d => addMonths(d, -1))} sx={{ minWidth: 44, minHeight: 44, flexShrink: 0 }}>
                    <NavigateBefore fontSize="small" />
                  </IconButton>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ textAlign: 'center', minWidth: 0, px: 0.5 }} noWrap>
                    {format(mobileMonthStart, 'MMMM yyyy')}
                  </Typography>
                  <IconButton size="small" onClick={() => setMobileMonthStart(d => addMonths(d, 1))} sx={{ minWidth: 44, minHeight: 44, flexShrink: 0 }}>
                    <NavigateNext fontSize="small" />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                    gap: 0.25,
                    width: '100%',
                    minWidth: 0,
                  }}
                >
                  {(() => {
                    const weekStartsOn = calendarSettings?.startOfWeek ?? 1;
                    // Must match grid weekStartsOn (0=Sun … 6=Sat). Hardcoded Mon–Sun headers
                    // made Sunday-start calendars look a day early.
                    const dayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
                    const headers = [
                      ...dayLabels.slice(weekStartsOn),
                      ...dayLabels.slice(0, weekStartsOn),
                    ];
                    return headers.map((label, i) => (
                      <Typography
                        key={`dow-${weekStartsOn}-${i}`}
                        variant="caption"
                        sx={{ textAlign: 'center', color: 'text.secondary', py: 0.5, aspectRatio: 'auto', minHeight: 'auto' }}
                      >
                        {label}
                      </Typography>
                    ));
                  })()}
                  {(() => {
                    const monthStart = startOfMonth(mobileMonthStart);
                    const monthEnd = endOfMonth(mobileMonthStart);
                    const weekStartsOn = calendarSettings?.startOfWeek ?? 1;
                    const calStart = startOfWeek(monthStart, { weekStartsOn });
                    const calEnd = endOfWeek(monthEnd, { weekStartsOn });
                    const calDays = eachDayOfInterval({ start: calStart, end: calEnd });
                    return calDays.map((d) => {
                      const hasEvents = calendarEvents.some((e) => {
                        const es = new Date(e.start);
                        const ee = new Date(e.end);
                        const ds = new Date(d);
                        ds.setHours(0, 0, 0, 0);
                        const de = new Date(d);
                        de.setHours(23, 59, 59, 999);
                        return es < de && ee > ds;
                      });
                      const isCurrentMonth = isSameMonth(d, monthStart);
                      const isToday = isSameDay(d, new Date());
                      return (
                        <Box
                          key={format(d, 'yyyy-MM-dd')}
                          onClick={() => setDayModal({ open: true, date: d })}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 1,
                            cursor: 'pointer',
                            touchAction: 'manipulation',
                            minHeight: 40,
                            aspectRatio: '1',
                            minWidth: 0,
                            bgcolor: isToday ? 'primary.main' : 'transparent',
                            color: isToday ? 'primary.contrastText' : isCurrentMonth ? 'text.primary' : 'text.disabled',
                            '&:active': { bgcolor: isToday ? 'primary.dark' : 'action.selected' },
                          }}
                        >
                          <Typography variant="body2" fontWeight={isToday ? 700 : 400} sx={{ fontSize: '0.8125rem', lineHeight: 1.2 }}>
                            {format(d, 'd')}
                          </Typography>
                          {hasEvents && <Box sx={{ display: 'flex', gap: 0.25, mt: 0.25 }}><Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: isToday ? 'primary.contrastText' : 'primary.main' }} /></Box>}
                        </Box>
                      );
                    });
                  })()}
                </Box>
              </>
            )}

            <DayBookingsModal open={dayModal.open} onClose={() => setDayModal({ open: false, date: null })} date={dayModal.date} events={calendarEvents} onEventClick={(event) => handleEventClick({ event })} getStatusColorForChip={getStatusColorForChip} />
          </Box>
        ) : (
        <>
        {/* Calendar Navigation Bar - shown for all views */}
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid', 
          borderColor: 'divider',
          backgroundColor: 'transparent',
          borderRadius: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          {/* Three equal sections: Prev/Next + Date (left) | Day/Week/Month (center) | Create New + Settings (right) */}
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            {/* First third: Prev/Next buttons and Date - left aligned */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => {
                  const prevDate = new Date(currentDate);
                  if (view === 'day') {
                    prevDate.setDate(prevDate.getDate() - 1);
                  } else if (view === 'week') {
                    prevDate.setDate(prevDate.getDate() - 7);
                  } else if (view === 'month') {
                    // For month view, ensure we're working with the first day of the month
                    const firstOfMonth = new Date(prevDate.getFullYear(), prevDate.getMonth(), 1);
                    firstOfMonth.setMonth(firstOfMonth.getMonth() - 1);
                    prevDate.setTime(firstOfMonth.getTime());
                  }
                  // Update state first
                  setCurrentDate(prevDate);
                  currentDateRef.current = prevDate;
                  // Update FullCalendar view if it exists
                  if (calendarRef.current) {
                    const calendarApi = calendarRef.current.getApi();
                    if (view === 'day') {
                      calendarApi.gotoDate(prevDate);
                    } else if (view === 'month') {
                      // For month view, use gotoDate with first day of the month to ensure it updates immediately
                      const firstOfPrevMonth = new Date(prevDate.getFullYear(), prevDate.getMonth(), 1);
                      calendarApi.gotoDate(firstOfPrevMonth);
                    } else {
                      calendarApi.prev();
                    }
                  }
                }}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <CaretLeft size={18} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  const nextDate = new Date(currentDate);
                  if (view === 'day') {
                    nextDate.setDate(nextDate.getDate() + 1);
                  } else if (view === 'week') {
                    nextDate.setDate(nextDate.getDate() + 7);
                  } else if (view === 'month') {
                    // For month view, ensure we're working with the first day of the month
                    const firstOfMonth = new Date(nextDate.getFullYear(), nextDate.getMonth(), 1);
                    firstOfMonth.setMonth(firstOfMonth.getMonth() + 1);
                    nextDate.setTime(firstOfMonth.getTime());
                  }
                  // Update state first
                  setCurrentDate(nextDate);
                  currentDateRef.current = nextDate;
                  // Update FullCalendar view if it exists
                  if (calendarRef.current) {
                    const calendarApi = calendarRef.current.getApi();
                    if (view === 'day') {
                      calendarApi.gotoDate(nextDate);
                    } else if (view === 'month') {
                      // For month view, use gotoDate with first day of the month to ensure it updates immediately
                      const firstOfNextMonth = new Date(nextDate.getFullYear(), nextDate.getMonth(), 1);
                      calendarApi.gotoDate(firstOfNextMonth);
                    } else {
                      calendarApi.next();
                    }
                  }
                }}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <CaretRight size={18} />
              </IconButton>
              <Typography 
                variant="h6" 
                fontWeight="bold"
                sx={{
                  whiteSpace: 'nowrap'
                }}
              >
                {view === 'week' ? (getWeekTitleFromDateRange() ?? formatDateWithOrdinal(currentDate, view)) : formatDateWithOrdinal(currentDate, view)}
              </Typography>
            </Box>

            {/* Second third: Day/Week/Month buttons - center aligned */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
              {/* Dropdown for small screens */}
              <FormControl 
                size="small" 
                sx={{ 
                  display: { xs: 'block', sm: 'none' },
                  minWidth: 100
                }}
              >
                <Select
                  value={view}
                  onChange={(e) => {
                    const newView = e.target.value;
                    setView(newView);
                    if (calendarRef.current) {
                      const calendarApi = calendarRef.current.getApi();
                      if (newView === 'day') {
                        const targetDate = getDayViewTargetDate();
                        setCurrentDate(targetDate);
                        currentDateRef.current = targetDate;
                        calendarApi.changeView('timeGridDay', targetDate);
                      } else if (newView === 'week') {
                        // Load this week when clicking week view
                        const today = new Date();
                        // Get Monday of current week
                        const day = today.getDay();
                        const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
                        const monday = new Date(today.setDate(diff));
                        monday.setHours(0, 0, 0, 0);
                        setCurrentDate(monday);
                        currentDateRef.current = monday;
                        calendarApi.changeView('timeGridWeek', monday);
                      } else if (newView === 'month') {
                        // When switching to month view, use today's date to show the current month
                        const today = new Date();
                        const firstOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                        setCurrentDate(firstOfCurrentMonth);
                        currentDateRef.current = firstOfCurrentMonth;
                        calendarApi.changeView('dayGridMonth', firstOfCurrentMonth);
                      }
                    }
                  }}
                  sx={{
                    fontSize: '0.75rem',
                    borderRadius: 0.25,
                  }}
                >
                  <MenuItem value="day">Day</MenuItem>
                  <MenuItem value="week">Week</MenuItem>
                  <MenuItem value="month">Month</MenuItem>
                </Select>
              </FormControl>

              {/* Buttons for larger screens */}
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.5 }}>
                <Button
                  variant={view === 'day' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setView('day');
                    const targetDate = getDayViewTargetDate();
                    setCurrentDate(targetDate);
                    currentDateRef.current = targetDate;
                    if (calendarRef.current) {
                      const calendarApi = calendarRef.current.getApi();
                      calendarApi.changeView('timeGridDay', targetDate);
                    }
                  }}
                  size="small"
                  sx={{
                    minWidth: 60,
                    fontSize: '0.75rem',
                    backgroundColor: view === 'day' ? '#E91E63' : 'transparent',
                    color: view === 'day' ? 'white' : 'text.primary',
                    borderRadius: 0.25,
                    borderColor: view === 'day' ? undefined : 'grey.400',
                    '&:hover': {
                      backgroundColor: view === 'day' ? '#C2185B' : 'action.hover',
                      borderColor: view === 'day' ? undefined : 'grey.500',
                    },
                  }}
                >
                  Day
                </Button>
                <Button
                  variant={view === 'week' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setView('week');
                    // Load this week when clicking week view
                    const today = new Date();
                    // Get Monday of current week
                    const day = today.getDay();
                    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
                    const monday = new Date(today.setDate(diff));
                    monday.setHours(0, 0, 0, 0);
                    setCurrentDate(monday);
                    currentDateRef.current = monday;
                    if (calendarRef.current) {
                      const calendarApi = calendarRef.current.getApi();
                      calendarApi.changeView('timeGridWeek', monday);
                    }
                  }}
                  size="small"
                  sx={{
                    minWidth: 60,
                    fontSize: '0.75rem',
                    backgroundColor: view === 'week' ? '#E91E63' : 'transparent',
                    color: view === 'week' ? 'white' : 'text.primary',
                    borderRadius: 0.25,
                    borderColor: view === 'week' ? undefined : 'grey.400',
                    '&:hover': {
                      backgroundColor: view === 'week' ? '#C2185B' : 'action.hover',
                      borderColor: view === 'week' ? undefined : 'grey.500',
                    },
                  }}
                >
                  Week
                </Button>
                <Button
                  variant={view === 'month' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setView('month');
                    // When switching to month view, use today's date to show the current month
                    const today = new Date();
                    const firstOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                    setCurrentDate(firstOfCurrentMonth);
                    currentDateRef.current = firstOfCurrentMonth;
                    if (calendarRef.current) {
                      const calendarApi = calendarRef.current.getApi();
                      calendarApi.changeView('dayGridMonth', firstOfCurrentMonth);
                    }
                  }}
                  size="small"
                  sx={{
                    minWidth: 60,
                    fontSize: '0.75rem',
                    backgroundColor: view === 'month' ? '#E91E63' : 'transparent',
                    color: view === 'month' ? 'white' : 'text.primary',
                    borderRadius: 0.25,
                    borderColor: view === 'month' ? undefined : 'grey.400',
                    '&:hover': {
                      backgroundColor: view === 'month' ? '#C2185B' : 'action.hover',
                      borderColor: view === 'month' ? undefined : 'grey.500',
                    },
                  }}
                >
                  Month
                </Button>
              </Box>
            </Box>

            {/* Third third: Create New and Booking Settings - right aligned */}
            {userType === 'supplier' && (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2 }}>
              {/* Filter by appointment type / event tag */}
              <Button
                variant="outlined"
                endIcon={<CaretDown size={16} />}
                onClick={(e) => setTypeFilterAnchorEl(e.currentTarget)}
                sx={{
                  px: 2,
                  py: 1,
                  fontSize: '0.875rem',
                  borderRadius: 1,
                  textTransform: 'none',
                  color: 'text.primary',
                  borderColor: 'divider',
                  '&:hover': { borderColor: 'text.primary', backgroundColor: 'action.hover' },
                }}
              >
                {typeFilters.length === 0 ? 'Filter By Type' : `Types (${typeFilters.length})`}
              </Button>
              <Menu
                anchorEl={typeFilterAnchorEl}
                open={Boolean(typeFilterAnchorEl)}
                onClose={() => setTypeFilterAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { maxHeight: 420, minWidth: 240 } } }}
              >
                <MenuItem
                  dense
                  disabled={typeFilters.length === 0}
                  onClick={() => setTypeFilters([])}
                >
                  Show all
                </MenuItem>
                {serviceTypesList.length > 0 && (
                  <ListSubheader disableSticky sx={{ lineHeight: '32px' }}>Appointment types</ListSubheader>
                )}
                {serviceTypesList.map((serviceType) => {
                  const filterKey = `svc:${normaliseId(serviceType._id)}`;
                  return (
                    <MenuItem key={filterKey} dense onClick={() => toggleTypeFilter(filterKey)}>
                      <Checkbox size="small" checked={typeFilters.includes(filterKey)} sx={{ p: 0.5, mr: 0.5 }} />
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: serviceType.color || '#1976d2',
                          border: '1px solid rgba(0,0,0,0.2)',
                          mr: 1,
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2" noWrap>{serviceType.name}</Typography>
                    </MenuItem>
                  );
                })}
                {eventTagOptions.length > 0 && (
                  <ListSubheader disableSticky sx={{ lineHeight: '32px' }}>Event tags</ListSubheader>
                )}
                {eventTagOptions.map((tag) => {
                  const filterKey = `tag:${tag.key}`;
                  return (
                    <MenuItem key={filterKey} dense onClick={() => toggleTypeFilter(filterKey)}>
                      <Checkbox size="small" checked={typeFilters.includes(filterKey)} sx={{ p: 0.5, mr: 0.5 }} />
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: tag.color,
                          border: '1px solid rgba(0,0,0,0.2)',
                          mr: 1,
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2" noWrap>{tag.name}</Typography>
                    </MenuItem>
                  );
                })}
                {serviceTypesList.length === 0 && eventTagOptions.length === 0 && (
                  <MenuItem dense disabled>No appointment types or event tags yet</MenuItem>
                )}
              </Menu>
              {/* Create New Dropdown */}
              <Button
                variant="contained"
                endIcon={<CaretDown size={16} />}
                onClick={handleCreateNewClick}
                sx={{
                  px: 2,
                  py: 1,
                  fontSize: '0.875rem',
                  backgroundColor: '#000',
                  color: '#fff',
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: '#333',
                  },
                }}
              >
                Create New
              </Button>
              <Menu
                anchorEl={createNewAnchorEl}
                open={createNewMenuOpen}
                onClose={handleCreateNewClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem onClick={handleCreateNewAppointment}>
                  <Plus size={20} style={{ marginRight: 8 }} />
                  New Appointment
                </MenuItem>
                <MenuItem onClick={handleCreateNewEvent}>
                  <CalendarCheck size={20} style={{ marginRight: 8 }} />
                  New Event
                </MenuItem>
              </Menu>
              
            </Box>
          )}
          </Box>
        </Box>

        {/* Calendar Content */}
        <CardContent sx={{ 
          p: 0,
          pb: view === 'day' && userType === 'supplier' ? 0 : undefined,
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          backdropFilter: 'blur(10px)',
          // Override theme rule that makes nested Cards solid white
          '& .MuiCard-root': {
            backgroundColor: 'transparent',
            backdropFilter: 'none',
          },
          // Override last-child padding for day view
          ...(view === 'day' && userType === 'supplier' && {
            '&:last-child': {
              paddingBottom: '0 !important',
            },
            // Override border-bottom-color rule that's making borders invisible - multiple selectors for specificity
            '& .MuiContainer-root .MuiCard-root .MuiBox-root': {
              borderBottomColor: 'rgba(0, 0, 0, 0.1) !important',
            },
            '& .day-view-time-column .day-view-time-slot': {
              borderBottomColor: 'rgba(0, 0, 0, 0.1) !important',
            },
            '& .day-view-time-column': {
              '& .MuiBox-root': {
                borderBottomColor: 'rgba(0, 0, 0, 0.1) !important',
              },
            },
          }),
        }}>
          {/* Custom Day View with Staff */}
          {view === 'day' && userType === 'supplier' ? (
            <>
              <style>
                {`
                  /* Override theme Card.js overrides for day view time column */
                  .css-1k08pve-MuiCardContent-root .MuiContainer-root .MuiCard-root .day-view-time-column,
                  .css-1k08pve-MuiCardContent-root .MuiContainer-root .MuiCard-root .day-view-time-column .day-view-time-slot,
                  .css-1k08pve-MuiCardContent-root .MuiCard-root .day-view-time-column,
                  .css-1k08pve-MuiCardContent-root .MuiCard-root .day-view-time-column .day-view-time-slot,
                  .MuiCardContent-root .MuiContainer-root .MuiCard-root .day-view-time-column,
                  .MuiCardContent-root .MuiContainer-root .MuiCard-root .day-view-time-column .day-view-time-slot,
                  .MuiCardContent-root .MuiCard-root .day-view-time-column,
                  .MuiCardContent-root .MuiCard-root .day-view-time-column .day-view-time-slot {
                    border-bottom-color: rgba(0, 0, 0, 0.1) !important;
                    border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
                  }
                `}
              </style>
              <Box sx={{ 
                p: 0, 
                backgroundColor: 'transparent',
                // Force border visibility on time slots
                '& .day-view-time-column .day-view-time-slot': {
                  borderBottom: '1px solid rgba(0, 0, 0, 0.1) !important',
                },
                '& .day-view-time-column': {
                  borderBottom: '1px solid rgba(0, 0, 0, 0.1) !important',
                  '& > .MuiBox-root': {
                    borderBottom: '1px solid rgba(0, 0, 0, 0.1) !important',
                  },
                },
              }}>
                {renderDayViewWithStaff()}
              </Box>
            </>
          ) : (
            <Box sx={{ 
            position: 'relative',
            '& .fc': {
              fontFamily: theme.typography.fontFamily
            },
            '& .fc-header-toolbar': {
              padding: '16px',
              marginBottom: 0
            },
            '& .fc-daygrid-day': {
              minHeight: '100px',
              borderRight: '1px solid rgba(0, 0, 0, 0.1)',
              backgroundColor: '#ffffff',
            },
            '& .fc-daygrid-day-frame': {
              minHeight: '100px'
            },
            '& .fc-day': {
              minHeight: '100px'
            },
            '& .fc-event-blocking': {
              borderLeft: '4px solid rgba(0, 0, 0, 0.35)',
              fontWeight: 'bold',
              opacity: 0.9
            },
            '& .fc-event-simultaneous': {
              borderLeft: '2px dashed rgba(0, 0, 0, 0.35)',
              opacity: 0.8
            },
            '& .fc-event': {
              cursor: 'pointer',
              borderRadius: '4px',
              padding: '2px 4px',
              fontSize: '0.75rem',
              border: 'none',
              marginBottom: '2px',
              '&:hover': {
                opacity: 0.9,
                transform: 'translateY(-1px)',
                boxShadow: theme.shadows[2]
              },
              '& *': {
                color: '#000000 !important' // Black text for all event content
              }
            },
            '& .fc-day-today': {
              backgroundColor: '#ffffff !important', // White background even for today
            },
            // Dark pink border for today in month view
            '& .fc-daygrid-day.fc-day-today-border': {
              border: '2px solid #C2185B !important', // Dark pink border
              boxSizing: 'border-box',
            },
            '& .fc-daygrid-day.fc-day-today-border .fc-daygrid-day-frame': {
              border: '2px solid #C2185B !important', // Dark pink border on frame
            },
            '& .fc-button': {
              backgroundColor: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              color: 'white',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark
              }
            },
            '& .fc-button-primary:not(:disabled):active, .fc-button-primary:not(:disabled).fc-button-active': {
              backgroundColor: theme.palette.primary.dark,
              borderColor: theme.palette.primary.dark
            },
            '& .fc-col-header-cell': {
              padding: '8px 4px',
              fontWeight: 400,
              fontSize: '0.875rem',
              backgroundColor: theme.palette.background.default,
              textAlign: 'left !important',
            },
            '& .fc-timegrid-axis': {
              fontSize: '0.75rem',
              fontWeight: 400,
            },
            '& .fc-timegrid-slot-label': {
              fontSize: '0.75rem',
              fontWeight: 400,
            },
            '& .fc-timegrid-axis-cushion': {
              fontSize: '0.75rem',
              fontWeight: 400,
            },
            '& .fc-all-day': {
              fontSize: '0.75rem',
              fontWeight: 400,
            },
            '& .fc-daygrid-day-number': {
              padding: '4px',
              fontWeight: 400,
              fontSize: '0.875rem'
            },
            '& .fc-timegrid-slot': {
              height: '48px',
              borderTop: '1px solid rgba(0, 0, 0, 0.1)',
              backgroundColor: '#ffffff',
            },
            '& .fc-timegrid-slot-lane': {
              borderTop: 'none !important',
            },
            '& .fc-timegrid-col': {
              borderRight: '1px solid rgba(0, 0, 0, 0.1)',
              backgroundColor: '#ffffff',
            },
            // Remove row-level hover - we want individual lane hover only
            '& .fc-timegrid-col:hover': {
              backgroundColor: 'transparent',
            },
            '& .fc-timegrid-slot:hover': {
              backgroundColor: 'transparent',
            },
            // Individual lane hover - only for enabled lanes
            '& .fc-timegrid-slot-lane:not(.fc-timegrid-slot-disabled):hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              cursor: 'pointer',
            },
            // Grid lines for week view
            '& .fc-timeGridWeek-view .fc-timegrid-slot': {
              borderTop: '1px solid rgba(0, 0, 0, 0.1) !important',
            },
            '& .fc-timeGridWeek-view .fc-timegrid-col': {
              borderRight: '1px solid rgba(0, 0, 0, 0.1) !important',
            },
            '& .fc-daygrid-day:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
              cursor: 'pointer',
            },
            '& .fc-button-primary': {
              backgroundColor: '#E91E63 !important',
              borderColor: '#C2185B !important',
              color: '#fff !important',
              '&:hover': {
                backgroundColor: '#C2185B !important',
                borderColor: '#AD1457 !important',
              },
              '&:focus': {
                backgroundColor: '#C2185B !important',
                borderColor: '#AD1457 !important',
                boxShadow: '0 0 0 0.2rem rgba(233, 30, 99, 0.25) !important',
              },
            },
            '& .fc-button-active': {
              backgroundColor: '#AD1457 !important',
              borderColor: '#880E4F !important',
              '&:hover': {
                backgroundColor: '#880E4F !important',
                borderColor: '#6A0D3A !important',
              },
            },
            '& .fc-button:disabled': {
              backgroundColor: '#F8BBD0 !important',
              borderColor: '#F48FB1 !important',
              color: '#fff !important',
              opacity: 0.6,
            },
            // Day header styling - two-line format
            '& .fc-day-header-content': {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start !important',
              lineHeight: 1.2,
              textAlign: 'left !important',
            },
            // White background for calendar
            backgroundColor: '#ffffff',
            '& .fc-scrollgrid': {
              backgroundColor: '#ffffff',
            },
            '& .fc-timegrid-divider': {
              display: 'none',
            },
            '& .fc-day-header-dayname': {
              fontSize: '0.875rem !important',
              fontWeight: '400 !important',
              color: 'rgba(0, 0, 0, 1) !important', // Black for all day names
            },
            '& .fc-day-header-dayname.fc-day-header-today': {
              color: '#C2185B !important', // Dark pink for today
              fontWeight: 'bold !important',
            },
            // Pink border for today in week view header (on the th element)
            '& .fc-col-header-cell.fc-col-header-today-border': {
              border: '2px solid #E91E63 !important', // Pink border
              borderRadius: '4px',
            },
            '& .fc-day-header-date': {
              fontSize: '0.75rem !important',
              fontWeight: '400 !important',
              color: 'rgba(0, 0, 0, 0.6) !important', // Grey for date
              marginTop: '2px',
            },
            // Past disabled days: pink + not clickable
            '& .fc-daygrid-day.fc-day-disabled': {
              backgroundColor: '#ffebee !important',
              opacity: 1,
            },
            '& .fc-daygrid-day.fc-day-disabled.fc-day-past': {
              pointerEvents: 'none !important',
              cursor: 'not-allowed !important',
            },
            '& .fc-daygrid-day.fc-day-disabled.fc-day-past .fc-event-unassigned, & .fc-daygrid-day.fc-day-disabled.fc-day-past .fc-event-unassigned *': {
              pointerEvents: 'none !important',
            },
            // Future closed days: muted styling, whole cell clickable for supplier-created bookings.
            '& .fc-daygrid-day.fc-day-no-shifts': {
              backgroundColor: `${theme.palette.action.hover} !important`,
              opacity: 1,
              cursor: 'pointer',
            },
            // Stretch the day-number link over the entire cell so clicking anywhere triggers dateClick
            '& .fc-daygrid-day.fc-day-no-shifts .fc-daygrid-day-frame': {
              position: 'relative',
            },
            '& .fc-daygrid-day.fc-day-no-shifts .fc-daygrid-day-number': {
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2,
              display: 'block',
            },
            // Events must sit above the stretched day-number link so they are clickable on closed days.
            '& .fc-daygrid-day.fc-day-no-shifts .fc-daygrid-day-events': {
              position: 'relative',
              zIndex: 3,
              pointerEvents: 'auto !important',
            },
            '& .fc-daygrid-day.fc-day-no-shifts .fc-event': {
              position: 'relative',
              zIndex: 4,
              pointerEvents: 'auto !important',
              cursor: 'pointer !important',
            },
            '& .fc-daygrid-day.fc-day-no-shifts .fc-event *': {
              pointerEvents: 'auto !important',
              cursor: 'pointer !important',
            },
            // Allow clicking on events in day cells (FullCalendar default blocks pointer events)
            '& .fc-daygrid-day-events': {
              pointerEvents: 'all !important',
              cursor: 'pointer !important',
            },
            // Ensure all slot elements are clickable by default
            '& .fc-timeGridWeek-view td.fc-timegrid-slot': {
              pointerEvents: 'auto !important',
            },
            '& .fc-timeGridDay-view td.fc-timegrid-slot': {
              pointerEvents: 'auto !important',
            },
            // Default enabled slots styling - target individual td elements with high specificity
            '& .fc-timeGridWeek-view td.fc-timegrid-slot-lane:not(.fc-timegrid-slot-disabled)': {
              backgroundColor: 'transparent !important',
              opacity: '1 !important',
              cursor: 'pointer !important',
              pointerEvents: 'auto !important',
              transition: 'background-color 0.2s',
            },
            '& .fc-timeGridDay-view td.fc-timegrid-slot-lane:not(.fc-timegrid-slot-disabled)': {
              backgroundColor: 'transparent !important',
              opacity: '1 !important',
              cursor: 'pointer !important',
              pointerEvents: 'auto !important',
              transition: 'background-color 0.2s',
            },
            // Style disabled individual day cells within time slot rows in week view
            // But allow clicking on future disabled slots for force booking
            '& .fc-timeGridWeek-view .fc-slot-day-cell.fc-slot-day-disabled': {
              backgroundColor: `${theme.palette.action.hover} !important`,
              opacity: 0.6,
              cursor: 'pointer !important',
            },
            // Prevent clicking on past disabled slots
            '& .fc-timeGridWeek-view .fc-slot-day-cell.fc-slot-day-disabled.fc-slot-past': {
              pointerEvents: 'none !important',
              cursor: 'not-allowed !important',
            },
            // Style for unassigned bookings (dashed border, amber/yellow color)
            '& .fc-event-unassigned': {
              borderStyle: 'dashed !important',
              borderWidth: '2px !important',
              pointerEvents: 'auto !important', // Ensure unassigned events are always clickable
              cursor: 'pointer !important',
            },
            // Ensure unassigned events in month view are clickable even if day cell is disabled
            '& .fc-dayGridMonth-view .fc-event-unassigned': {
              pointerEvents: 'auto !important',
              cursor: 'pointer !important',
              zIndex: 10, // Ensure events are above disabled day backgrounds
            },
            // Make sure event elements themselves are clickable
            '& .fc-event-unassigned .fc-event-main': {
              pointerEvents: 'auto !important',
              cursor: 'pointer !important',
            },
            // Hide avatars in day cells for week view (they should only be in column headers)
            '& .fc-timeGridWeek-view .fc-daygrid-day .fc-day-header-content > div:last-child': {
              display: 'none !important',
            },
          }}>
            <Box sx={{ 
              position: 'relative'
            }}>
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                key={businessId || 'supplier-calendar'}
                initialView={getCalendarView()}
                headerToolbar={false}
                eventDisplay="block"
                events={calendarEvents}
                height="auto"
                eventClick={handleEventClick}
                eventMouseEnter={handleCalendarEventMouseEnter}
                eventMouseLeave={handleCalendarEventMouseLeave}
                eventDidMount={(info) => {
                  const tooltipData = getCalendarEventTooltipText(info.event);
                  info.el.setAttribute('aria-label', `${tooltipData.title}. Assignees: ${tooltipData.assignees}`);
                }}
                dateClick={handleDateClick}
                selectable={true}
                selectMirror={true}
                selectMinDistance={10}
                select={handleSelect}
                initialDate={currentDate}
                // Remove validRange to allow navigation to past months for viewing
                // Past date restrictions are handled in handleDateClick and handleSelect instead
                datesSet={handleDatesSet}
                firstDay={calendarSettings?.startOfWeek ?? 1}
                slotLaneClassNames={getSlotLaneClassNames}
                slotLaneContent={(slotInfo) => {
                  // For week view, create 7 divs (one for each day) inside each time slot row
                  if (userType === 'supplier') {
                    const calendarApi = calendarRef.current?.getApi();
                    if (calendarApi?.view?.type === 'timeGridWeek') {
                      // Get time from slotInfo - try multiple sources
                      let timeStr = '';
                      if (slotInfo?.time && typeof slotInfo.time === 'string' && slotInfo.time !== '') {
                        timeStr = slotInfo.time;
                      } else if (slotInfo?.timeText) {
                        // Fallback to timeText if time is not available
                        timeStr = slotInfo.timeText;
                      }
                      // If still no time, it will be extracted from the row in useEffect
                      
                      // Get the week start date from the calendar view
                      const view = calendarApi.view;
                      if (view && view.activeStart) {
                        const weekStart = new Date(view.activeStart);
                        // Adjust to the configured first day of week
                        const firstDay = calendarSettings?.startOfWeek ?? 1;
                        const weekStartDay = weekStart.getDay();
                        const daysToSubtract = (weekStartDay - firstDay + 7) % 7;
                        weekStart.setDate(weekStart.getDate() - daysToSubtract);
                        weekStart.setHours(0, 0, 0, 0);
                        
                        // Create 7 divs, one for each day of the week
                        const dayDivs = [];
                        for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
                          const dayDate = new Date(weekStart);
                          dayDate.setDate(weekStart.getDate() + dayIndex);
                          const dateStr = `${dayDate.getFullYear()}-${String(dayDate.getMonth() + 1).padStart(2, '0')}-${String(dayDate.getDate()).padStart(2, '0')}`;
                          
                          dayDivs.push(
                            `<div class="fc-slot-day-cell" data-slot-date="${dateStr}" data-slot-time="${timeStr}" style="width: calc(100% / 7); height: 100%; display: inline-block; vertical-align: top; box-sizing: border-box;"></div>`
                          );
                        }
                        
                        return {
                          html: dayDivs.join('')
                        };
                      }
                    }
                  }
                  return null;
                }}
                dayCellDidMount={(info) => {
                  const dayDate = new Date(info.date);
                  // Supplier month view: today and future days are clickable; earlier days are not.
                  if (userType === 'supplier' && !isPastCalendarDay(dayDate)) {
                    const dateStr = info.date.toISOString().split('T')[0];
                    const handleDayClick = (e) => {
                      if (e.target.closest('.fc-event')) return; // let event click open the event/appointment
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(`/supplier/bookings/new?date=${dateStr}`);
                    };
                    info.el.addEventListener('click', handleDayClick);
                    info.el._dayClickCleanup = () => info.el.removeEventListener('click', handleDayClick);
                  }
                }}
                dayCellWillUnmount={(info) => {
                  if (info.el._dayClickCleanup) {
                    info.el._dayClickCleanup();
                    delete info.el._dayClickCleanup;
                  }
                }}
                dayCellClassNames={(dayInfo) => {
                  const dayDate = new Date(dayInfo.date);
                  
                  // Check if today
                  const today = new Date();
                  const isToday = format(dayDate, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd');
                  
                  // Past days: disabled and not clickable (fc-day-past gets pointer-events: none)
                  if (isPastCalendarDay(dayDate)) {
                    return 'fc-day-disabled fc-day-past';
                  }
                  
                  // Future closed days are still clickable for supplier-created bookings.
                  if (userType === 'supplier' && !hasShiftsForDay(dayInfo.date)) {
                    return 'fc-day-no-shifts';
                  }
                  
                  // Add dark pink border for today in month view
                  if (isToday) {
                    return 'fc-day-today-border';
                  }
                  
                  return '';
                }}
                dayCellContent={(dayInfo) => {
                  const dayDate = new Date(dayInfo.date);
                  const isPast = isPastCalendarDay(dayDate);
                  const hasShifts = hasShiftsForDay(dayInfo.date);
                  
                  // Check if we're in month view - only show avatars in month view, not week view
                  const currentView = view === 'month' ? 'dayGridMonth' : (view === 'week' ? 'timeGridWeek' : calendarRef.current?.getApi()?.view?.type);
                  const isMonthView = currentView === 'dayGridMonth';
                  
                  // Get staff members working on this day (only for month view)
                  let staffForDay = (userType === 'supplier' && !isPast && hasShifts && isMonthView) ? getStaffForDay(dayInfo.date) : [];
                  
                  // Filter by visibleUsers if specified
                  const visibleUsers = calendarSettings?.visibleUsers || [];
                  if (visibleUsers.length > 0 && teamMembers.length > 0 && visibleUsers.length < teamMembers.length) {
                    staffForDay = staffForDay.filter(member => {
                      // member._id is set to BusinessMember ID in fetchTeamMembers
                      const memberId = member._id || member.businessMember?._id || member.businessMember;
                      return memberId && visibleUsers.some(visibleId => 
                        memberId.toString() === visibleId.toString()
                      );
                    });
                  }
                  
                  // Build avatars HTML if there are staff members (only for month view)
                  let avatarsHtml = '';
                  if (staffForDay.length > 0 && isMonthView) {
                    const avatarSize = 18;
                    const avatarSpacing = 3;
                    avatarsHtml = `<div style="display: flex; gap: ${avatarSpacing}px; justify-content: center; margin-top: 2px; flex-wrap: wrap;">`;
                    staffForDay.slice(0, 3).forEach((member, _idx) => {
                      const memberName = getMemberDisplayName(member);
                      const avatarSrc = getStaffMemberDisplayPhoto(member);
                      const initials = memberName.charAt(0).toUpperCase();
                      const avatarUrl = avatarSrc ? getAvatarSrc(avatarSrc) : '';
                      
                      avatarsHtml += `<div style="width: ${avatarSize}px; height: ${avatarSize}px; border-radius: 50%; overflow: hidden; border: 1.5px solid #E91E63; background-color: #E91E63; display: flex; align-items: center; justify-content: center; color: white; font-size: 9px; font-weight: bold;" title="${memberName}">`;
                      if (avatarUrl) {
                        avatarsHtml += `<img src="${avatarUrl}" alt="${memberName}" style="width: 100%; height: 100%; object-fit: cover;" />`;
                      } else {
                        avatarsHtml += initials;
                      }
                      avatarsHtml += `</div>`;
                    });
                    if (staffForDay.length > 3) {
                      avatarsHtml += `<div style="width: ${avatarSize}px; height: ${avatarSize}px; border-radius: 50%; background-color: rgba(0,0,0,0.1); display: flex; align-items: center; justify-content: center; font-size: 9px; font-weight: bold;" title="+${staffForDay.length - 3} more">+${staffForDay.length - 3}</div>`;
                    }
                    avatarsHtml += `</div>`;
                  }
                  
                  // Add title attribute for disabled days
                  let dayHtml = dayInfo.dayNumberText;
                  if (isPast) {
                    dayHtml = `<div title="Cannot schedule in the past">${dayInfo.dayNumberText}</div>`;
                  } else if (userType === 'supplier' && !hasShifts) {
                    dayHtml = `<div title="Business closed">${dayInfo.dayNumberText}</div>`;
                  }
                  
                  return {
                    html: `${dayHtml}${avatarsHtml}`
                  };
                }}
                dayHeaderContent={(dateInfo) => {
                  const _dayOfWeek = dateInfo.date.getDay();
                  const dayName = format(dateInfo.date, 'EEEE'); // Full day name
                  const dateStr = format(dateInfo.date, 'd MMM'); // Date like "29 Dec"
                  
                  // Check if today
                  const today = new Date();
                  const isToday = format(dateInfo.date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd');
                  
                  // Determine color class - only today gets green, all others black
                  let colorClass = 'fc-day-header-weekday';
                  if (isToday) {
                    colorClass = 'fc-day-header-today';
                  }
                  
                  // Get staff members working on this day (for month and week views)
                  // Check if we're in month or week view
                  const currentView = calendarRef.current?.getApi()?.view?.type;
                  const isMonthView = currentView === 'dayGridMonth';
                  const isWeekView = currentView === 'timeGridWeek';
                  let staffForDay = (userType === 'supplier' && (isMonthView || isWeekView)) ? getStaffForDay(dateInfo.date) : [];
                  
                  // Filter by visibleUsers if specified
                  const visibleUsers = calendarSettings?.visibleUsers || [];
                  if (visibleUsers.length > 0 && teamMembers.length > 0 && visibleUsers.length < teamMembers.length) {
                    staffForDay = staffForDay.filter(member => {
                      // member._id is set to BusinessMember ID in fetchTeamMembers
                      const memberId = member._id || member.businessMember?._id || member.businessMember;
                      return memberId && visibleUsers.some(visibleId => 
                        memberId.toString() === visibleId.toString()
                      );
                    });
                  }
                  
                  // Generate avatar HTML (for month and week views)
                  let avatarsHtml = '';
                  if (staffForDay.length > 0) {
                    const avatarSize = isWeekView ? 20 : 24; // Smaller for week view
                    const avatarSpacing = isWeekView ? 3 : 4;
                    avatarsHtml = `<div style="display: flex; gap: ${avatarSpacing}px; justify-content: ${isWeekView ? 'flex-start' : 'center'}; margin-top: 4px; flex-wrap: wrap;">`;
                    staffForDay.slice(0, isWeekView ? 4 : 5).forEach((member, _idx) => {
                      const memberName = getMemberDisplayName(member);
                      const avatarSrc = getStaffMemberDisplayPhoto(member);
                      const initials = memberName.charAt(0).toUpperCase();
                      const avatarUrl = avatarSrc ? getAvatarSrc(avatarSrc) : '';
                      
                      avatarsHtml += `<div style="width: ${avatarSize}px; height: ${avatarSize}px; border-radius: 50%; overflow: hidden; border: 2px solid #E91E63; background-color: #E91E63; display: flex; align-items: center; justify-content: center; color: white; font-size: ${isWeekView ? '9px' : '10px'}; font-weight: bold;" title="${memberName}">`;
                      if (avatarUrl) {
                        avatarsHtml += `<img src="${avatarUrl}" alt="${memberName}" style="width: 100%; height: 100%; object-fit: cover;" />`;
                      } else {
                        avatarsHtml += initials;
                      }
                      avatarsHtml += `</div>`;
                    });
                    if (staffForDay.length > (isWeekView ? 4 : 5)) {
                      avatarsHtml += `<div style="width: ${avatarSize}px; height: ${avatarSize}px; border-radius: 50%; background-color: rgba(0,0,0,0.1); display: flex; align-items: center; justify-content: center; font-size: ${isWeekView ? '9px' : '10px'}; font-weight: bold;" title="+${staffForDay.length - (isWeekView ? 4 : 5)} more">+${staffForDay.length - (isWeekView ? 4 : 5)}</div>`;
                    }
                    avatarsHtml += `</div>`;
                  }
                  
                  // For month view, only show day name, not date
                  // For week view, show day name and date
                  const dateHtml = isMonthView ? '' : `<div class="fc-day-header-date">${dateStr}</div>`;
                  
                  return {
                    html: `<div class="fc-day-header-content">
                      <div class="fc-day-header-dayname ${colorClass}">${dayName}</div>
                      ${dateHtml}
                      ${avatarsHtml}
                    </div>`
                  };
                }}
                slotMinTime={timeGridSlotRange.min}
                slotMaxTime={timeGridSlotRange.max}
                scrollTime={openingHoursWindow ? `${openingHoursWindow.min}:00` : '08:00:00'}
                scrollTimeReset={false}
                allDaySlot={false}
                nowIndicator={true}
                dayMaxEvents={true}
                moreLinkClick="popover"
                timeZone="local"
                eventTimeFormat={{
                  hour: 'numeric',
                  minute: '2-digit',
                  meridiem: 'short'
                }}
                displayEventTime={true}
                eventContent={renderCalendarEventContent}
              />
              <Popper
                open={eventTooltip.open}
                anchorEl={eventTooltip.anchorEl}
                placement="top"
                modifiers={[
                  { name: 'offset', options: { offset: [0, 8] } }
                ]}
                sx={{ zIndex: 2000, pointerEvents: 'none' }}
              >
                <Paper
                  elevation={6}
                  sx={{
                    px: 1.25,
                    py: 0.75,
                    maxWidth: 280,
                    backgroundColor: 'grey.900',
                    color: 'common.white',
                    borderRadius: 1
                  }}
                >
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, lineHeight: 1.2 }}>
                    {eventTooltip.title}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.2, opacity: 0.9 }}>
                    Assignee{eventTooltip.assignees && eventTooltip.assignees.includes(',') ? 's' : ''}: {eventTooltip.assignees}
                  </Typography>
                </Paper>
              </Popper>
              {loading && (
                <Box sx={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  right: 0, 
                  bottom: 0, 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  pointerEvents: 'none'
                }}>
                  <CircularProgress size={28} />
                </Box>
              )}
            </Box>
          </Box>
          )}
        </CardContent>
        </>
        )}
      </Card>

      {renderBookingDialog()}
      
      {/* Booking editing dialog */}
      <Dialog
        open={editTeamMembersOpen}
        onClose={() => setEditTeamMembersOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit booking</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
            Location and notes
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="calendar-edit-location-type-label">Location type</InputLabel>
                <Select
                  labelId="calendar-edit-location-type-label"
                  value={editLocationType}
                  label="Location type"
                  onChange={(e) => setEditLocationType(e.target.value)}
                >
                  <MenuItem value="on_site">On-site</MenuItem>
                  <MenuItem value="off_site">Off-site</MenuItem>
                  <MenuItem value="virtual">Virtual</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              {editLocationType === 'virtual' ? (
                <TextField
                  fullWidth
                  size="small"
                  label="Video call link"
                  placeholder="https://meet.example.com/..."
                  value={editVideoLink}
                  onChange={(e) => setEditVideoLink(e.target.value)}
                  helperText="Use Save and update couple to email the meeting link."
                />
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label="Location"
                  placeholder="Address or meeting point"
                  value={editLocationAddress}
                  onChange={(e) => setEditLocationAddress(e.target.value)}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Internal notes"
                multiline
                rows={3}
                value={editSupplierNotes}
                onChange={(e) => setEditSupplierNotes(e.target.value)}
                placeholder="Private notes for your team. Not shared with the couple."
              />
            </Grid>
          </Grid>

          {editBookingError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditBookingError('')}>
              {editBookingError}
            </Alert>
          )}
          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            Team members
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
            Select team members to assign to this booking:
          </Typography>
          
          {availableTeamMembersForEdit.length === 0 ? (
            <Alert severity="info">
              No team members are available for the selected time slot.
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {availableTeamMembersForEdit.map((member) => {
                const memberId = member.id?.toString ? member.id.toString() : member.id;
                const isSelected = selectedTeamMembersForEdit.some(id => 
                  (id?.toString ? id.toString() : id) === memberId
                );
                return (
                  <Grid item xs={12} sm={6} md={4} key={memberId}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: isSelected ? 2 : 1,
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        '&:hover': { borderColor: 'primary.main' }
                      }}
                      onClick={() => {
                        const isCurrentlySelected = selectedTeamMembersForEdit.some(id => 
                          (id?.toString ? id.toString() : id) === memberId
                        );
                        if (isCurrentlySelected) {
                          setSelectedTeamMembersForEdit(prev => prev.filter(id => 
                            (id?.toString ? id.toString() : id) !== memberId
                          ));
                        } else {
                          setSelectedTeamMembersForEdit(prev => [...prev, memberId]);
                        }
                      }}
                    >
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {member.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {member.role}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <CheckCircle 
                            size={16} 
                            color={isSelected ? '#4caf50' : '#ccc'} 
                          />
                          <Typography variant="body2" fontWeight={isSelected ? 'bold' : 'normal'}>
                            {isSelected ? 'Selected' : 'Available'}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setEditTeamMembersOpen(false)} disabled={editBookingSaving}>
            Cancel
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            onClick={() => handleSaveBookingEdit(false)}
            disabled={editBookingSaving}
          >
            {editBookingSaving ? 'Saving…' : 'Save'}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSaveBookingEdit(true)}
            disabled={editBookingSaving || !bookingHasCoupleContact(selectedBooking)}
          >
            {editBookingSaving ? 'Saving…' : 'Save and update couple'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={editBookingToast.open}
        autoHideDuration={4000}
        onClose={() => setEditBookingToast({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setEditBookingToast({ open: false, message: '' })}
          severity="success"
          sx={{ width: '100%' }}
        >
          {editBookingToast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UnifiedCalendar;
